'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Lock, CheckCircle2, ChevronLeft, ChevronRight, Award, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveAssetUrl } from '@/lib/utils/resolve-asset-url';
// Shared thumbnail component (created 2026-05-14 for tickets 099475cd + 1af309c2).
// Single source of truth for thumbnail rendering — never ships <img src=""> /
// <img src={null}>; falls back to a deterministic letter-avatar placeholder.
import { CourseThumbnail } from '@/components/lms/course-thumbnail';
import type { BadgePack } from '@/lib/gamification/types';
import { NEWS_CATEGORY, type NewsItemProp, type TrainingSlideProp, type CourseCardProp, type PointsHistoryProp, type BadgeCardProp, type CertificateCardProp, type LevelDataProp } from '@/lib/user-portal/transform-portal-data';
import { todayInZone } from '@/lib/time/zoned-day';
import { browserTimeZone } from '@/lib/time/zone';
import { ManagerDashboardContent } from '@/app/settings/portal/_components/manager/manager-dashboard-content';
// FIX (portal-manager-fix-wave-1, 2026-05-12): SAMPLE_LEADERBOARD removed.
// `lib/portal/sample-data.ts` documents its own architectural rule that the
// live employee portal must NEVER read these fallbacks. This file IS the
// live employee surface. Empty leaderboard data now renders an empty state
// instead of fake names like "Kartik Zoho".
import { LeaderboardScopeToggle, type LeaderboardScope } from '@/components/portal/leaderboard-scope-toggle';
import { getMonogram } from '@/lib/utils/get-monogram';
import { resolveTheme } from '@/lib/user-portal/resolve-theme';
import { useTranslation } from '@/lib/i18n';
import { useTextDirection } from '@/lib/i18n/use-text-direction';
import { LanguageSwitcher } from '@/components/language-switcher';
import {
  enrollmentStatusLabel,
  portalCopy,
  portalTimeAgo,
  riskLevelLabel,
  useLearnerFormat,
} from '@/components/user-portal/learner-i18n';

type Translate = (key: string, params?: Record<string, string | number>) => string;

// FIX: Bug 2 — /Level badges/level-N.png files don't exist in public/.
// Replaced hardcoded paths with null; render sites now show a tier-coloured
// numeric badge via the LevelBadgeFallback component below.
// Shown only when the workspace has no badge pack's levels; named in the
// reader's language (a pack's own level names are data and stay as written).
const FALLBACK_LEVELS = [
  { id: 1, labelKey: 'portal.home.levels.beginner', pointsRequired: 200 },
  { id: 2, labelKey: 'portal.home.levels.intermediate', pointsRequired: 400 },
  { id: 3, labelKey: 'portal.home.levels.advanced', pointsRequired: 800 },
  { id: 4, labelKey: 'portal.home.levels.expert', pointsRequired: 1200 },
  { id: 5, labelKey: 'portal.home.levels.master', pointsRequired: 1600 },
];

// Helper: strip HTML tags + collapse whitespace so news descriptions stored
// as rich-text snippets (e.g. `<p>Test content</p>`) render as readable
// plain text inside the line-clamped card preview. Done in JS because the
// description is a short summary, not the full body — DOMPurify-grade
// sanitisation would be overkill and would also strip the &amp; entity etc.
function htmlToPlainText(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/<\/?[^>]+(>|$)/g, ' ') // drop tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

// FIX (2026-05-14, ticket 9b36b6ae): Trainings card CTA was hardcoded to
// "Start Training" regardless of enrollment status. Branch on the canonical
// Title Case enrollment status enum so the CTA reads correctly for every
// row (Not Started → "Start Training", In Progress → "Continue Training",
// Completed → "Review Training", Overdue → "Resume Training").
function trainingCtaCopy(t: Translate, status: string): string {
  switch (status) {
    case 'Completed':
      return t('portal.home.cta.review');
    case 'In Progress':
      return t('portal.home.cta.continue');
    case 'Overdue':
      return t('portal.home.cta.resume');
    case 'Not Started':
    default:
      return t('portal.home.cta.start');
  }
}

// FIX (2026-05-14, ticket 696c6b46): Important Updates training card body
// + progress bar were hardcoded to a 35%/0% ternary on `status === 'In Progress'`.
// Result: Completed rows fell to the else branch and showed "Not started" + 0%
// while the badge correctly showed "Completed". Both now derive from the same
// status field with real progress when available.
function trainingProgressBody(t: Translate, status: string, progress: number | undefined): {
  bodyText: string;
  progressValue: number;
} {
  // Clamp progress to [0,100] so a stray BE value (e.g. 110) never overflows
  // the bar visually.
  const clamped = typeof progress === 'number'
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : 0;
  if (status === 'Completed') return { bodyText: t('portal.home.progressBody.completed'), progressValue: 100 };
  if (status === 'In Progress')
    return {
      bodyText: t('portal.home.progressBody.percent', { percent: clamped > 0 ? clamped : 1 }),
      progressValue: clamped > 0 ? clamped : 1,
    };
  if (status === 'Overdue')
    return { bodyText: t('portal.home.progressBody.percentOverdue', { percent: clamped }), progressValue: clamped };
  // Not Started / unknown → 0%
  return { bodyText: t('portal.home.progressBody.notStarted'), progressValue: 0 };
}

/**
 * Reading time for a news item, from its words (was a fixed "2 min read" on
 * every article). ~200 words a minute, never less than one.
 */
function readingMinutes(html: string | null | undefined): number {
  const words = htmlToPlainText(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A training's due date: the workspace's calendar day (dueDay, 'YYYY-MM-DD')
 * and the zone it is a day in.
 */
type DueFields = { dueDay?: string; dueTimeZone?: string };

/**
 * Whole days from today to a training's due day (0 = due today, negative =
 * overdue), or null when it has none. `dueDay` is the workspace's calendar
 * day, so today is that zone's today too: the viewer's own made a learner east
 * of the workspace read "Overdue" while the day was still running there (an
 * Indian learner of a Dubai company from 00:00 IST, 1h30 early). The viewer's
 * zone only when none was known.
 */
function daysUntilDue(item: DueFields): number | null {
  if (!item.dueDay) return null;
  const due = Date.parse(item.dueDay);
  const today = Date.parse(todayInZone(item.dueTimeZone ?? browserTimeZone()));
  return Number.isFinite(due) && Number.isFinite(today) ? Math.round((due - today) / DAY_MS) : null;
}

// Helper: tier-coloured numeric badge used whenever a level image is missing.
function LevelBadgeFallback({ levelId, className }: { levelId: number; className?: string }) {
  const tierClass =
    levelId === 1 ? 'bg-amber-700' :
    levelId === 2 ? 'bg-zinc-400' :
    levelId === 3 ? 'bg-yellow-500' :
    levelId === 4 ? 'bg-cyan-600' :
    'bg-violet-600';
  return (
    <div className={cn(
      'rounded-full flex items-center justify-center text-white font-bold',
      tierClass,
      className,
    )}>
      {levelId}
    </div>
  );
}

export interface NewsItem {
  category: string;
  title: string;
  description: string;
  image: string;
}

export interface PolicyItem {
  category: string;
  title: string;
  description: string;
  image: string;
  /**
   * Where the card opens. The live portal feeds Policies from the knowledge
   * library (knowledge wave K1), and each card opens its document there
   * (`/portal/library?open=<id>`). Absent on the customizer's sample cards,
   * which open the reader as before.
   */
  href?: string;
  /** When the document was last updated (ISO); dates the card like a news item. */
  publishedAt?: string;
}

export interface UserProgress {
  currentLevel: number;
  currentPoints: number;
  achievedBadgeIds: string[];
}

// Empty user-progress used when no real progress data is provided.
// Live employee paths derive currentPoints / achievedBadgeIds from real
// hooks; admin customizer paths supply DEFAULT_USER_PROGRESS from
// `lib/portal/sample-data.ts` explicitly. There is no silent demo fallback.
const EMPTY_USER_PROGRESS: UserProgress = {
  currentLevel: 0,
  currentPoints: 0,
  achievedBadgeIds: [],
};

// FIX a3cdc486: resolved theme colors so the preview applies actual brand
// colors instead of always falling back to the app's default CSS variables.
export interface ThemeColorOverride {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  headerColor?: string;
  textColor?: string;
}

export interface PortalPreviewProps {
  selectedTheme: string;
  /** Resolved PortalTheme color fields — when provided, the preview header
   *  uses the theme's actual headerColor/primaryColor so each theme card's
   *  Preview shows distinct brand colours instead of always rendering navy. */
  themeColors?: ThemeColorOverride;
  inline?: boolean;
  previewDarkMode: boolean;
  onPreviewDarkModeToggle: () => void;
  onClose: () => void;
  portalLogoLight: string | null;
  portalLogoDark: string | null;
  heroHeading: string;
  heroDescription: string;
  heroImage: string | null;
  ctaButtonText: string;
  ctaButtonLink?: string;
  coverImage: string | null;
  showStatsBar: boolean;
  showImportantUpdates: boolean;
  showAssignedTraining: boolean;
  showNewsUpdates: boolean;
  showPolicies: boolean;
  showProgressSection: boolean;
  showYourRank: boolean;
  showPoints: boolean;
  showBadges: boolean;
  showCertificates: boolean;
  showRiskScore: boolean;
  showCompromised: boolean;
  showAcknowledged: boolean;
  showEvaluated: boolean;
  showTrainings: boolean;
  showLeaderboard: boolean;
  showPointsTab: boolean;
  showBadgesTab: boolean;
  showCertificatesTab: boolean;
  isManagerView: boolean;
  onManagerViewChange: (checked: boolean) => void;
  managerViewTab: 'dashboard' | 'myteam';
  onManagerViewTabChange: (tab: 'dashboard' | 'myteam') => void;
  /**
   * Admin-JWT manager identity. When provided, the manager body branch
   * (ManagerDashboardContent) fetches via /v1/team-portal/view-as/<id>
   * instead of the portal-JWT /v1/portal/team/dashboard endpoint. Used
   * by /teams-portal/view-as/[managerId] (admin impersonation). Omit for
   * live portal callers — ManagerDashboardContent falls back to the
   * portal-JWT path.
   */
  managerId?: string;
  // Teams portal customisation (passed through to ManagerDashboardContent)
  showManagerProfile?: boolean;
  showDesignation?: boolean;
  showLeaderboardRank?: boolean;
  showTotalPoints?: boolean;
  showTeamStructure?: boolean;
  showDirectReports?: boolean;
  showIndirectReports?: boolean;
  showManagersUnder?: boolean;
  showHierarchyButton?: boolean;
  showPhishingPerformance?: boolean;
  showPhishingKPIs?: boolean;
  showPhishingFunnel?: boolean;
  showPhishingBreakdown?: boolean;
  showTrainingPerformance?: boolean;
  showTrainingKPIs?: boolean;
  showTopCompleters?: boolean;
  showNeedsImprovement?: boolean;
  showAnnouncementPerformance?: boolean;
  showAnnouncementKPIs?: boolean;
  showAnnouncementFunnel?: boolean;
  showGamification?: boolean;
  showLevels?: boolean;
  showBadgeDistribution?: boolean;
  showTeamLeaderboard?: boolean;
  showMyTeamLeaderboard?: boolean;
  showMyTeamRiskScore?: boolean;
  showMyTeamPhishing?: boolean;
  showMyTeamTraining?: boolean;
  showMyTeamAnnouncement?: boolean;
  // Data overrides (defaults to hardcoded preview data when not provided)
  newsItems?: NewsItem[];
  policyItems?: PolicyItem[];
  userProgress?: UserProgress;
  // Employee-specific data (optional — defaults to demo values for customize preview)
  employeeName?: string;
  employeeAvatar?: string;
  employeeEmail?: string;
  employeeDepartment?: string;
  employeeRank?: number;
  employeeTotalPoints?: number;
  employeeBadgeCount?: number;
  employeeBadges?: string[];
  employeeRiskLevel?: 'High' | 'Medium' | 'Low';
  employeeTimesCompromised?: number;
  employeeTrainingCompletion?: number;
  employeeReportRate?: number;
  /**
   * BUG-513 — real per-recipient phishing funnel (from
   * GET /v1/recipients/:id/phishing-summary). When provided the phishing
   * stat cards render these EXACT numbers instead of synthesising fake
   * figures from `employeeTimesCompromised`. Absent on the settings-portal
   * theme preview (no specific recipient) — that path keeps the synthetic
   * sample so the theme is still illustratable.
   */
  phishingStats?: {
    sent: number;
    opened: number;
    clicked: number;
    compromised: number;
    reported: number;
    trained: number;
    evaluated: number;
    campaignCount: number;
    lastIncident: { campaignName: string; date: string } | null;
  } | null;
  isManager?: boolean;
  leaderboardData?: Array<{ rank: number; name: string; dept: string; points: number; isCurrentUser: boolean }>;
  // Real data overrides (from JSON stores)
  trainingSlides?: TrainingSlideProp[];
  courseCards?: CourseCardProp[];
  pointsHistory?: PointsHistoryProp[];
  badgeCards?: BadgeCardProp[];
  certificateCards?: CertificateCardProp[];
  activeBadgePack?: BadgePack | null;
  levelData?: LevelDataProp[];
  /**
   * The learner's Challenges section (item 3) — the employee portal passes
   * it; the admin previews and the builder do not.
   */
  challengesSection?: React.ReactNode;
  /**
   * Extra controls for the header, before the language switcher: the live
   * employee portal passes its "Ask AI" button (the policy assistant); the
   * admin previews and the builder pass nothing.
   */
  headerActions?: React.ReactNode;
  /**
   * Workspace-level benchmarks for the Compromised / Acknowledged / Evaluated
   * Performance cards. Populated by admin callers (settings/portal,
   * settings/team-portal, portal-preview/user-portal/[id]) via
   * usePortalBenchmarks(). When absent the "vs. Team avg" / "Last incident" /
   * "Last activity" side meta stays empty — used to be hardcoded literals
   * (teamAvg=18/48/52 + "Credential Harvest Sim" / "Q1 Phishing Drill"),
   * fixed 2026-05-12.
   */
  benchmarks?: import('@/lib/portal/benchmarks-types').PortalBenchmarks | null;
  /**
   * Portal tab to activate on first render.
   * 0 = Trainings, 1 = Leaderboard, 2 = Points, 3 = Badges, 4 = Certificates.
   * Used by subroute redirects (e.g. /portal/badges → /portal?section=badges)
   * to land on the right bottom-strip tab.
   */
  initialActivePortalTab?: number;
}

export function PortalPreview(props: PortalPreviewProps) {
  // FIX #681 #682: use Next.js client router so training navigation preserves
  // React Query cache + Zustand store state (hard window.location.href kills both).
  const router = useRouter();
  const { t } = useTranslation();
  const fmt = useLearnerFormat();
  // The carousel slides along the reading direction (it moves the strip with a
  // transform, which the layout direction does not mirror by itself).
  const direction = useTextDirection();

  const {
    selectedTheme, inline, previewDarkMode, onPreviewDarkModeToggle, onClose,
    portalLogoLight, portalLogoDark,
    heroHeading, heroDescription, heroImage, ctaButtonText, ctaButtonLink, coverImage,
    showStatsBar, showImportantUpdates, showAssignedTraining, showNewsUpdates, showPolicies,
    showProgressSection, showYourRank, showPoints, showBadges, showCertificates,
    showRiskScore, showCompromised, showAcknowledged, showEvaluated,
    showTrainings, showLeaderboard, showPointsTab, showBadgesTab, showCertificatesTab,
    isManagerView, onManagerViewChange, managerViewTab, onManagerViewTabChange,
    // FIX a3cdc486: theme color overrides from the resolved PortalTheme object.
    themeColors,
  } = props;

  // Derive theme-brand CSS overrides. When themeColors is provided we inject
  // the theme's actual hex values as inline styles on the portal header banner
  // so each theme's Preview dialog shows its distinct brand colour instead of
  // always rendering the admin app's default dark navy.
  const headerBrandStyle: React.CSSProperties = themeColors
    ? { backgroundColor: themeColors.headerColor || themeColors.primaryColor || undefined }
    : {};

  // No silent demo fallbacks — when a caller does not provide data we render
  // empty arrays / zero counters and the component's existing empty-state UI
  // takes over. Admin customizer callsites explicitly pass DEFAULT_NEWS_ITEMS
  // / DEFAULT_POLICY_ITEMS / DEFAULT_USER_PROGRESS from `lib/portal/sample-data.ts`.
  const newsItems = props.newsItems ?? [];
  const policyItems = props.policyItems ?? [];
  const userProgress = props.userProgress ?? EMPTY_USER_PROGRESS;

  // Employee-specific derived values — empty/zero when no real data is supplied.
  const employeeName = props.employeeName ?? '';
  const employeeFirstName = employeeName ? employeeName.split(' ')[0] : '';
  const employeeAvatar = props.employeeAvatar ?? '';
  const employeeMonogram = getMonogram(employeeName);
  const employeeDepartment = props.employeeDepartment ?? '';
  const employeeRank = props.employeeRank ?? 0;
  const employeeTotalPoints = props.employeeTotalPoints ?? userProgress.currentPoints;
  const employeeBadgeCount = props.employeeBadgeCount ?? userProgress.achievedBadgeIds.length;
  // NOTE (tickets 898 / 1166): the portal only receives the coarse risk BAND
  // (Low / Medium / High) — the real numeric 0-100 risk score is NOT part of
  // the portal-dashboard DTO (BE selects `recipients.riskStatus`, a band enum,
  // not a numeric column). We therefore MUST NOT render a precise-looking
  // number here — doing so made a "Low" user always read exactly "25" while the
  // admin surface showed the real computed percentage (e.g. 38%), so the two
  // never matched. `employeeRiskScore` below is used ONLY to position the gauge
  // needle inside the coloured arc — it is never shown as a number. The widget
  // now displays the band LABEL so it is honestly read as a coarse band.
  const employeeRiskLevelLabel: 'High' | 'Medium' | 'Low' | null =
    props.employeeRiskLevel ?? null;
  const employeeRiskScore =
    props.employeeRiskLevel === 'High' ? 85
    : props.employeeRiskLevel === 'Medium' ? 55
    : props.employeeRiskLevel === 'Low' ? 25
    : 0;
  const employeeTimesCompromised = props.employeeTimesCompromised ?? 0;
  const employeeTrainingCompletion = props.employeeTrainingCompletion ?? 0;
  const employeeReportRate = props.employeeReportRate ?? 0;

  // Resolved real-data props with hardcoded fallbacks
  const resolvedLevels =
    props.levelData ??
    FALLBACK_LEVELS.map((level) => ({
      id: level.id,
      name: t(level.labelKey),
      pointsRequired: level.pointsRequired,
      badge: null as string | null,
    }));
  const resolvedBadgePack = props.activeBadgePack ?? null;

  const [isRoadmapDrawerOpen, setIsRoadmapDrawerOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<BadgeCardProp | null>(null);
  const [selectedNews, setSelectedNews] = useState<NewsItemProp | null>(null);
  // A policy card from the knowledge library opens its document in the
  // library; anything else (news, the customizer's sample policies) opens the
  // reader sheet.
  const openUpdate = (slide: { type: 'news' | 'policy'; href?: string }) => {
    if (slide.type === 'policy' && slide.href) {
      router.push(slide.href);
      return;
    }
    setSelectedNews(slide as unknown as NewsItemProp);
  };
  const [activePortalTab, setActivePortalTab] = useState(props.initialActivePortalTab ?? 0);
  const [currentNewsSlide, setCurrentNewsSlide] = useState(0);

  // Tab controls state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterValue, setFilterValue] = useState('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // FIX (2026-05-14, ticket af89916d): "View More" was a dead button
  // (onclick=false). It now reveals the next page of training cards on
  // each click instead of doing nothing.
  const TRAININGS_PAGE_SIZE = 6;
  const [trainingsVisibleCount, setTrainingsVisibleCount] = useState(TRAININGS_PAGE_SIZE);

  // Leaderboard scope — preview-only toggle so the admin can see how each scope
  // renders. Does not send a live BE request; the preview always uses sample data.
  const [leaderboardScope, setLeaderboardScope] = useState<LeaderboardScope>('Workspace');

  // Reset controls when switching tabs
  const handleTabChange = (idx: number) => {
    setActivePortalTab(idx);
    setViewMode('grid');
    setFilterValue('all');
    setSortOrder('asc');
  };

  // FIX (2026-05-14, ticket 81d34965): "Explore Courses" hero CTA used to be
  // a dead <Button> when admin hadn't configured a custom ctaButtonLink.
  // The CTA now activates the Trainings tab + scrolls to its anchor — same
  // behavior as the bottom-strip "Trainings" tab button. Custom ctaButtonLink
  // (admin-configured external URL) still wins when set.
  //
  // FIX (2026-05-14, ticket af89916d): "View More" button at the bottom of
  // the Trainings list shares this handler — both should land the user on the
  // full trainings tab view.
  const scrollToTrainings = useCallback(() => {
    setActivePortalTab(0);
    setViewMode('grid');
    setFilterValue('all');
    setSortOrder('asc');
    // Defer to next paint so the tab content mounts before scrolling.
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        const el = document.getElementById('portal-section-tabs');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  // Feature-section tab visibility, indexed to match activePortalTab (0..4)
  const tabVisibility = [showTrainings, showLeaderboard, showPointsTab, showBadgesTab, showCertificatesTab];
  const anyTabVisible = tabVisibility.some(Boolean);
  useEffect(() => {
    if (anyTabVisible && !tabVisibility[activePortalTab]) {
      const firstVisible = tabVisibility.findIndex(Boolean);
      if (firstVisible >= 0) setActivePortalTab(firstVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrainings, showLeaderboard, showPointsTab, showBadgesTab, showCertificatesTab, activePortalTab]);

  // Tab-specific config
  const tabHasGridView = [true, false, false, true, true]; // Trainings, Badges, Certificates
  const all = { value: 'all', label: t('portal.home.filter.all') };
  const tabFilterOptions: Record<number, { value: string; label: string }[]> = {
    0: [all, { value: 'in-progress', label: t('portal.home.filter.inProgress') }, { value: 'not-started', label: t('portal.home.filter.notStarted') }, { value: 'completed', label: t('portal.home.filter.completed') }],
    1: [all],
    2: [all, { value: 'earned', label: t('portal.home.filter.earned') }, { value: 'spent', label: t('portal.home.filter.spent') }],
    3: [all, { value: 'earned', label: t('portal.home.filter.earned') }, { value: 'locked', label: t('portal.home.filter.locked') }],
    4: [all, { value: 'completed', label: t('portal.home.filter.completed') }, { value: 'in-progress', label: t('portal.home.filter.inProgress') }],
  };
  const byName: [string, string] = [t('portal.home.sort.nameAsc'), t('portal.home.sort.nameDesc')];
  const tabSortLabel: Record<number, [string, string]> = {
    0: byName,
    1: [t('portal.home.sort.pointsAsc'), t('portal.home.sort.pointsDesc')],
    2: [t('portal.home.sort.newest'), t('portal.home.sort.oldest')],
    3: byName,
    4: byName,
  };
  // The bottom-strip tabs, indexed like activePortalTab.
  const tabNames = [
    t('portal.home.tabs.trainings'),
    t('portal.home.tabs.leaderboard'),
    t('portal.home.tabs.points'),
    t('portal.home.tabs.badges'),
    t('portal.home.tabs.certificates'),
  ];

  // The stock hero copy every workspace is seeded with reads in the learner's
  // language; wording an admin chose is shown as written (portalCopy).
  const shownHeroHeading = portalCopy(t, 'heroHeading', heroHeading);
  const shownHeroDescription = portalCopy(t, 'heroDescription', heroDescription);
  const shownCtaText = portalCopy(t, 'ctaButtonText', ctaButtonText);

  // transformNewsToProps files every article under NEWS_CATEGORY (the sample
  // data under the English "News Updates"); an admin's own category (policy
  // items) is shown as written.
  const newsCategoryLabel = (category: string) =>
    category === NEWS_CATEGORY || category === 'News Updates'
      ? t('portal.home.updates.newsCategory')
      : category;
  // A due date on screen: the workspace's day in the reader's language
  // ("Sep 30, 2026" / "30 سبتمبر 2026"); '' when there is none.
  const dueLabel = (item: DueFields) => (item.dueDay ? fmt.day(item.dueDay) : '');
  // The live portal's news rows carry publishedAt (NewsItemProp); the admin
  // customizer's sample items do not.
  const newsDate = (item: object) => fmt.date((item as { publishedAt?: string }).publishedAt);

  // Parallax scroll tracking
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      setScrollY(scrollRef.current.scrollTop);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const logo = previewDarkMode ? portalLogoDark : portalLogoLight;
  // When the admin chose "use same logo for both" and the customer's logo is
  // dark-on-transparent (very common for black wordmark PNGs), the logo
  // vanishes on the dark background. Detect the same-logo case and wrap in
  // a subtle light backdrop so the brand mark stays visible. If the customer
  // uploaded a dedicated dark variant (`portalLogoDark !== portalLogoLight`)
  // they explicitly designed it for dark mode — no backdrop needed.
  const logoNeedsLightBackdrop =
    previewDarkMode &&
    !!portalLogoLight &&
    !!portalLogoDark &&
    portalLogoLight === portalLogoDark;

  // Theme system reduced to Light + Dark only (ticket #190, 2026-05-08).
  // The product brand is pure black & white — no teal/blue/amber accents.
  // ALL themes (Light, Dark, and any future custom palettes) render through
  // the canonical welcome-hero layout below. The dark/light split is purely
  // a colour swap driven by `previewDarkMode`. The previous shadcn-black
  // early-return + modern-teal layout conditional are gone — both forced
  // admins to compare against alternate layouts that aren't part of the
  // product anymore. The legacy 'modern-teal' / 'shadcn-black' slugs are
  // kept as internal `selectedTheme` identifiers so existing customer rows
  // keep working; user-facing labels are "Light" + "Dark".
  const { previewTheme } = resolveTheme(selectedTheme, previewDarkMode);

  const currentLevel = userProgress.currentLevel;

  return (
    <div
      ref={scrollRef}
      data-theme-override={previewDarkMode ? "dark" : "light"}
      // FIX: Tailwind's dark mode is class-based (`@custom-variant dark
      // (&:is(.dark *))`) — toggling `data-theme-override` alone never
      // triggered the dark CSS-var swap (--background etc.) or any
      // `dark:` utility inside the preview, so users selecting "Dark"
      // in the Default Theme picker saw the preview stay light.
      // Adding the `dark` class when previewDarkMode is true flips the
      // CSS vars + activates every nested `dark:` utility.
      className={cn(
        "h-full w-full bg-background overflow-y-auto",
        previewDarkMode && "dark",
      )}
    >
      {/* Skip-to-content link — first focusable element so keyboard/screen-reader
          users can bypass the sticky header banner and land on the main content.
          Visually hidden until focused (sr-only + focus:not-sr-only). Targets the
          #portal-main-content region below. On the /portal dashboard the (portal)
          layout suppresses its own header/nav, so this component owns the page
          landmarks (banner / navigation / contentinfo) + the skip link. */}
      <a
        href="#portal-main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-[60] focus:rounded-md focus:bg-foreground focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-background focus:shadow-lg"
      >
        {t('portal.home.skipToContent')}
      </a>

      {/* Header with back button — hidden in inline mode */}
      {!inline && (
        <div className="sticky top-0 z-50 bg-background border-b border-border px-6 py-3 shadow-sm h-[50px] flex items-center">
          <div className="flex items-center justify-between w-full">
            <Button variant="outline" size="sm" onClick={onClose} className="flex items-center gap-2 text-foreground">
              <ArrowLeft className="w-4 h-4 rtl-flip" />
              {t('portal.home.backToSettings')}
            </Button>
          </div>
        </div>
      )}

      {/* Header Banner - sticky below back button bar */}
      {/* FIX a3cdc486: headerBrandStyle injects the theme's primaryColor when provided */}
      {/* role="banner" landmark — this is the portal's site header (brand + profile).
          On the /portal dashboard the (portal) layout suppresses its own <header>,
          so this element provides the banner landmark for screen readers. */}
      <header
        role="banner"
        className={cn(
          "sticky z-40 h-16 border-b border-border bg-card flex items-center justify-between px-8",
          "backdrop-blur-xl transition-shadow duration-300",
          scrollY > 10 ? "shadow-sm" : "shadow-none",
          inline ? "top-0" : "top-[50px]"
        )}
        style={headerBrandStyle}
      >
            <div className="flex items-center gap-2">
              {logo ? (
                <img
                  src={logo}
                  alt={t('portal.home.companyLogo')}
                  className={cn(
                    "h-10 w-auto object-contain",
                    logoNeedsLightBackdrop && "bg-white/95 rounded px-2 py-1",
                  )}
                />
              ) : (
                // The product name: never translated, always left to right. A
                // flex row runs right to left on an Arabic page, which read
                // "® Firewall Human" (knowledge polish, 2026-10-04).
                <div className="flex items-center gap-1" dir="ltr">
                  <span className="text-xl font-bold text-foreground">Human</span>
                  <span className="text-xl font-bold text-foreground">Firewall</span>
                  <span className="text-sm text-muted-foreground">®</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Manager View toggle — visible ONLY when the previewed
                  employee is explicitly flagged as a manager. Default
                  changed from `!== false` (shown by default) to `=== true`
                  (hidden by default) per ticket #209: "Manager view toggle
                  will be visible only if the employee is manager itself." */}
              {props.isManager === true && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted">
                <span className="text-xs font-medium text-foreground">{t('portal.home.managerView')}</span>
                <Switch
                  aria-label={t('portal.home.managerView')}
                  checked={isManagerView}
                  className="h-4 w-8 data-[state=checked]:bg-primary"
                  onCheckedChange={(checked) => {
                    onManagerViewChange(checked);
                    if (checked) onManagerViewTabChange('dashboard');
                  }}
                />
              </div>
              )}

              {props.headerActions}

              {/* The global header controls are hidden on /portal: the portal
                  header carries its own language switcher (locale audit
                  2026-09-26, L-02). */}
              <LanguageSwitcher />

              <Button
                variant="ghost"
                size="icon"
                // Aria-label added 2026-05-14 to close ticket #309 — the
                // sun/moon icon-only button had no accessible name, so
                // screen readers AND Playwright couldn't find it (the
                // reporter's complaint "clicking the moon icon does
                // nothing" may have been about the wrong button entirely).
                // With a stable aria-label, the toggle is now reliably
                // testable + named.
                // FIX #685: On the real portal (inline=true) the label must not
                // say "preview" — "preview" only makes sense in the admin builder.
                aria-label={
                  inline
                    ? previewDarkMode ? t('portal.home.switchToLight') : t('portal.home.switchToDark')
                    : previewDarkMode ? t('portal.home.switchPreviewToLight') : t('portal.home.switchPreviewToDark')
                }
                className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                onClick={onPreviewDarkModeToggle}
              >
                {previewDarkMode ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                )}
              </Button>

              <div className="flex items-center gap-3">
                <div className="text-end hidden md:block">
                  <div className="text-sm font-semibold text-foreground" dir="auto">{employeeName}</div>
                  <div className="text-xs text-muted-foreground" dir="auto">{employeeDepartment}</div>
                </div>
                {employeeAvatar && employeeAvatar.trim() ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={employeeAvatar}
                    alt={t('portal.home.profile')}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-border"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full ring-2 ring-border bg-muted flex items-center justify-center text-xs font-semibold text-foreground">
                    {employeeMonogram}
                  </div>
                )}
              </div>
            </div>
          </header>

      {/* Preview Content */}
      {/* id="portal-main-content" is the skip-link target. We do NOT set role="main"
          here because the (portal) layout already wraps PortalPreview in a single
          <main> landmark — a second one would be an a11y violation. */}
      <div id="portal-main-content" className="w-full flex justify-center bg-background">
        <div className="w-full max-w-[1728px] relative">
          {/* Main Content */}
          {isManagerView ? (
            <ManagerDashboardContent
              activeTab={managerViewTab}
              onTabChange={onManagerViewTabChange}
              managerId={props.managerId}
              // previewMode is TRUE only in admin-builder context (not inline).
              // In live employee mode (`inline=true`) we set previewMode=false
              // so the weekly recap auto-opens, the hierarchy mock is hidden,
              // etc. Previously this was hardcoded `previewMode` which meant
              // live employees got admin-builder visualisations.
              previewMode={!inline}
              showManagerProfile={props.showManagerProfile}
              showDesignation={props.showDesignation}
              showLeaderboardRank={props.showLeaderboardRank}
              showTotalPoints={props.showTotalPoints}
              showTeamStructure={props.showTeamStructure}
              showDirectReports={props.showDirectReports}
              showIndirectReports={props.showIndirectReports}
              showManagersUnder={props.showManagersUnder}
              showHierarchyButton={props.showHierarchyButton}
              showPhishingPerformance={props.showPhishingPerformance}
              showPhishingKPIs={props.showPhishingKPIs}
              showPhishingFunnel={props.showPhishingFunnel}
              showPhishingBreakdown={props.showPhishingBreakdown}
              showTrainingPerformance={props.showTrainingPerformance}
              showTrainingKPIs={props.showTrainingKPIs}
              showTopCompleters={props.showTopCompleters}
              showNeedsImprovement={props.showNeedsImprovement}
              showAnnouncementPerformance={props.showAnnouncementPerformance}
              showAnnouncementKPIs={props.showAnnouncementKPIs}
              showAnnouncementFunnel={props.showAnnouncementFunnel}
              showGamification={props.showGamification}
              showLevels={props.showLevels}
              showBadgeDistribution={props.showBadgeDistribution}
              showTeamLeaderboard={props.showTeamLeaderboard}
              showMyTeamLeaderboard={props.showMyTeamLeaderboard}
              showMyTeamRiskScore={props.showMyTeamRiskScore}
              showMyTeamPhishing={props.showMyTeamPhishing}
              showMyTeamTraining={props.showMyTeamTraining}
              showMyTeamAnnouncement={props.showMyTeamAnnouncement}
            />
          ) : (
            <div className="flex justify-center bg-gradient-to-b from-background to-muted/20">
              <div className="w-full max-w-6xl px-8 py-10 space-y-10">
                {/* Background Cover Image with overlay */}
                <div
                  className="absolute start-0 end-0 top-16 h-[400px] bg-cover bg-center -z-10 opacity-10"
                  style={{
                    backgroundImage: `url(${coverImage || ''})`,
                  }}
                />

                {/* Canonical welcome-hero layout — rendered for every theme.
                    The previous `previewTheme === 'modern-teal' ? hero : alternate`
                    branch and the alternate "Modern Blue" layout below were
                    removed (ticket #190, 2026-05-08). Light/Dark only flips
                    colours via previewDarkMode, never layout. */}
                {true ? (
                  <div className="space-y-6">
                    {/* Hero Section */}
                    <div
                      className={cn(
                        "relative rounded-2xl overflow-hidden border shadow-none bg-muted/50",
                        previewDarkMode ? "border-white/[0.08]" : "border-black/[0.05]"
                      )}
                      style={{
                        transform: `translateY(${scrollY * 0.05}px)`,
                        transition: 'transform 0.1s linear',
                      }}
                    >
                      <div className="p-12">
                        <div className={cn(
                          "grid gap-12 items-center",
                          heroImage ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1",
                        )}>
                          <div className="space-y-6" style={{ transform: `translateY(${scrollY * -0.02}px)` }}>
                            <Badge variant="secondary" className="px-4 py-1.5 text-sm rounded-full">
                              {t('portal.home.greeting', { name: employeeFirstName })}
                            </Badge>
                            <h1 className="text-5xl font-bold leading-tight text-foreground" dir="auto">
                              {shownHeroHeading}
                            </h1>
                            {/* whitespace-pre-line preserves admin newlines;
                                break-words wraps long URLs / unbroken strings
                                so pasted prompts/links don't blow the layout. */}
                            <p className="text-muted-foreground text-base leading-relaxed whitespace-pre-line break-words line-clamp-6" dir="auto">
                              {shownHeroDescription}
                            </p>
                            {ctaButtonLink && ctaButtonLink !== '#' ? (
                              <Button asChild className="px-8 py-6 text-base font-medium">
                                <a href={ctaButtonLink} target="_blank" rel="noopener noreferrer">{shownCtaText}</a>
                              </Button>
                            ) : (
                              /* FIX (2026-05-14, ticket 81d34965): no custom link
                                 → activate Trainings tab + scroll. Was a dead
                                 button (onclick=false, href=null). */
                              <Button
                                className="px-8 py-6 text-base font-medium"
                                onClick={scrollToTrainings}
                              >
                                {shownCtaText}
                              </Button>
                            )}
                          </div>
                          {/* Hero image: render only when admin has set one.
                              Live employees should never see a placeholder
                              like "Hero image not set" — when unset, the
                              hero collapses to a single-column layout so
                              the welcome text takes full width. */}
                          {heroImage ? (
                            <div className="flex justify-center lg:justify-end" style={{ transform: `translateY(${scrollY * -0.08}px)` }}>
                              <div className="relative">
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={heroImage}
                                  alt={t('portal.home.heroImage')}
                                  className="w-80 h-80 rounded-full object-cover shadow-2xl"
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {/* Stats Section */}
                    {showStatsBar && (
                    <div
                      className="relative rounded-xl shadow-lg border border-border cursor-pointer transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]"
                      onClick={() => setIsRoadmapDrawerOpen(true)}
                    >
                      <Card className="border-0 shadow-none rounded-xl bg-card">
                        <CardContent className="px-12 py-8">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                            <div className="flex items-center gap-4">
                              {/* FIX: level badge PNGs replaced with numeric fallback */}
                              <LevelBadgeFallback levelId={currentLevel} className="w-20 h-20 text-2xl" />
                              <div>
                                <div className="page-heading text-foreground">{currentLevel}</div>
                                <div className="text-sm text-muted-foreground">{t('portal.home.level')}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <svg className={cn("w-16 h-16", employeeTotalPoints === 0 ? "text-muted-foreground/40" : "text-amber-500")} fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <div>
                                {/* FIX Bug 4: dim zero-state values + add earn-first hint */}
                                <div className={cn("page-heading", employeeTotalPoints === 0 ? "text-muted-foreground" : "text-foreground")}>
                                  {employeeTotalPoints === 0 ? '—' : fmt.number(employeeTotalPoints)}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {employeeTotalPoints === 0 ? t('portal.home.earnFirstPoints') : t('portal.home.rewardPoints')}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 justify-between">
                              <div>
                                {/* FIX Bug 4: dim zero-state badge count */}
                                <div className={cn("page-heading", employeeBadgeCount === 0 ? "text-muted-foreground" : "text-foreground")}>
                                  {employeeBadgeCount === 0 ? '—' : employeeBadgeCount}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {employeeBadgeCount === 0 ? t('portal.home.noBadgesYet') : t('portal.home.badges')}
                                </div>
                              </div>
                              {/* Compact 32px chips with overlap — was 56px which dominated the
                                  stat strip + made the badge row look like a hero element.
                                  Matches the canonical w-8 chip size used in the impersonation
                                  preview hero (line 306) and the small avatar chip pattern. */}
                              <div className="flex -space-x-1.5">
                                {(resolvedBadgePack?.badges.filter(b => !b.event.startsWith('level_')).slice(0, 6).map(b => b.icon) ?? []).map((src, i) => (
                                  src && src.trim() ? (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img key={i} src={src} alt={t('portal.home.badgeNumber', { number: i + 1 })} className="w-8 h-8 rounded-full ring-2 ring-background object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextSibling as HTMLElement | null)?.style && ((e.currentTarget.nextSibling as HTMLElement).style.display = 'flex'); }} />
                                  ) : (
                                    <div key={i} className="w-8 h-8 rounded-full ring-2 ring-background bg-muted flex items-center justify-center">
                                      <Award className="w-3.5 h-3.5 text-muted-foreground" />
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                        <div className="flex justify-end px-8 pb-4">
                          <Button variant="link" className="text-sm text-primary font-normal p-0 h-auto">
                            {t('portal.home.viewAll')}
                          </Button>
                        </div>
                      </Card>
                    </div>
                    )}

                    {/* Achievement Roadmap Drawer */}
                    <Sheet open={isRoadmapDrawerOpen} onOpenChange={setIsRoadmapDrawerOpen}>
                      <SheetContent side="end" className="!w-[560px] !max-w-[560px] overflow-y-auto p-6">
                        <SheetHeader>
                          <SheetTitle>{t('portal.home.roadmap.title')}</SheetTitle>
                          <SheetDescription>
                            {t('portal.home.roadmap.description')}
                          </SheetDescription>
                        </SheetHeader>

                        <div className="mt-6 p-5 bg-gradient-to-r from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30 rounded-xl">
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <div className="page-heading text-violet-600">{userProgress.currentLevel}</div>
                              <div className="text-sm text-muted-foreground">{t('portal.home.roadmap.currentLevel')}</div>
                            </div>
                            <div>
                              <div className="page-heading text-blue-600">{fmt.number(userProgress.currentPoints)}</div>
                              <div className="text-sm text-muted-foreground">{t('portal.home.roadmap.totalPoints')}</div>
                            </div>
                            <div>
                              <div className="page-heading text-green-600">{userProgress.achievedBadgeIds.length}</div>
                              <div className="text-sm text-muted-foreground">{t('portal.home.roadmap.badgesEarned')}</div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-8">
                          <h3 className="text-lg font-semibold mb-4">{t('portal.home.roadmap.levels')}</h3>
                          <div className="space-y-3">
                            {resolvedLevels.map((level) => {
                              const isAchieved = level.id <= userProgress.currentLevel;
                              const isNext = level.id === userProgress.currentLevel + 1;
                              return (
                                <Card key={level.id} className={cn(
                                  "p-4 transition-all rounded-xl",
                                  isAchieved ? "bg-card border-green-200" : "bg-muted/50 border-border opacity-60",
                                  isNext && "border-violet-300 border-2"
                                )}>
                                  <div className="flex items-center gap-4">
                                    <div className="relative">
                                      {level.badge && level.badge.trim() ? (
                                        // eslint-disable-next-line @next/next/no-img-element
                                        <img
                                          src={level.badge}
                                          alt={level.name}
                                          className={cn("w-16 h-16 object-contain", !isAchieved && "grayscale")}
                                        />
                                      ) : (
                                        <LevelBadgeFallback
                                          levelId={level.id}
                                          className={cn("w-16 h-16 text-xl", !isAchieved && "opacity-40")}
                                        />
                                      )}
                                      {!isAchieved && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <Lock className="w-6 h-6 text-gray-400" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-semibold" dir="auto">{level.name}</h4>
                                        {isAchieved && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                                        {isNext && <Badge variant="secondary" className="text-xs">{t('portal.home.roadmap.nextLevel')}</Badge>}
                                      </div>
                                      <p className="text-sm text-muted-foreground">
                                        {t('portal.home.roadmap.pointsRequired', { points: fmt.number(level.pointsRequired) })}
                                      </p>
                                      {isNext && (
                                        <div className="mt-2">
                                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                            <span>{t('portal.home.pts', { points: fmt.number(userProgress.currentPoints) })}</span>
                                            <span>{t('portal.home.pts', { points: fmt.number(level.pointsRequired) })}</span>
                                          </div>
                                          <Progress value={(userProgress.currentPoints / level.pointsRequired) * 100} className="h-2" />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Card>
                              );
                            })}
                          </div>
                        </div>

                        {resolvedBadgePack && resolvedBadgePack.badges.filter(b => !b.event.startsWith('level_')).length > 0 && (
                        <div className="mt-8">
                          <h3 className="text-lg font-semibold mb-4">{t('portal.home.roadmap.badges')}</h3>
                          <div className="grid grid-cols-2 gap-3">
                            {resolvedBadgePack.badges.filter(b => !b.event.startsWith('level_')).map((badge) => {
                              const isAchieved = userProgress.achievedBadgeIds.includes(badge.id);
                              const card: BadgeCardProp = { name: badge.name, icon: badge.icon, earned: isAchieved, description: badge.eventDescription };
                              return (
                                <Card
                                  key={badge.id}
                                  onClick={() => setSelectedBadge(card)}
                                  className={cn(
                                    "p-4 rounded-xl transition-all cursor-pointer hover:shadow-lg",
                                    isAchieved ? "bg-card border-green-200 dark:border-green-900/50" : "bg-muted/50 border-border opacity-60"
                                  )}
                                >
                                  <div className="flex flex-col items-center text-center gap-2">
                                    <div className="relative">
                                      {isAchieved ? (
                                        <>
                                          {badge.icon && badge.icon.trim() ? (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img src={badge.icon} alt={badge.name} className="w-16 h-16 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex'); }} />
                                          ) : null}
                                          {(!badge.icon || !badge.icon.trim()) && (
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                                              <Award className="w-8 h-8 text-amber-500" />
                                            </div>
                                          )}
                                          {badge.icon && badge.icon.trim() && (
                                            <div className="w-16 h-16 rounded-full bg-muted items-center justify-center hidden">
                                              <Award className="w-8 h-8 text-amber-500" />
                                            </div>
                                          )}
                                          <CheckCircle2 className="w-5 h-5 text-green-500 absolute -top-1 -end-1 bg-background rounded-full" />
                                        </>
                                      ) : (
                                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                                          <Lock className="w-6 h-6 text-muted-foreground" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="w-full">
                                      <h4 className="font-semibold text-sm text-foreground" dir="auto">{badge.name}</h4>
                                      <div className="mt-2 space-y-1">
                                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                          {isAchieved ? t('portal.home.badge.earnedFor') : t('portal.home.badge.howToEarn')}
                                        </div>
                                        <p className="text-xs text-foreground/80 leading-snug" dir="auto">
                                          {badge.eventDescription || t('portal.home.badge.defaultDescription')}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                        )}
                      </SheetContent>
                    </Sheet>

                    {/* Important Updates */}
                    {showImportantUpdates && (
                    <div id="portal-section-news" className="py-12 px-8 bg-background space-y-10">
                      <h2 className="page-heading text-foreground">{t('portal.home.updates.title')}</h2>

                      {/* Unified Carousel — Training + News Updates (2-up) */}
                      {(showAssignedTraining || showNewsUpdates || showPolicies) && (() => {
                        // Live employee paths must NEVER read hardcoded fallbacks
                        // (per architectural rule in lib/portal/sample-data.ts) —
                        // empty data renders an empty state, not fake titles.
                        const resolvedTrainingSlides = props.trainingSlides ?? [];
                        const trainingSlides = showAssignedTraining ? resolvedTrainingSlides.map(s => ({ type: 'training' as const, ...s })) : [];
                        const newsSlides = showNewsUpdates ? newsItems.map(n => ({ type: 'news' as const, ...n })) : [];
                        const policySlides = showPolicies ? policyItems.map(p => ({ type: 'policy' as const, ...p })) : [];
                        const allSlides = [...trainingSlides, ...newsSlides, ...policySlides];
                        const maxPage = Math.max(0, Math.ceil(allSlides.length / 2) - 1);
                        return (
                          <div className="relative">
                            {/* Outside the cards in both directions: each button
                                sits on a logical edge and is pushed outwards, the
                                way that edge faces ([&:dir(rtl)] mirrors the push;
                                translate-x is not mirrored by the layout). */}
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('portal.home.updates.previous')}
                              className="absolute start-0 top-1/2 -translate-y-1/2 -translate-x-14 [&:dir(rtl)]:translate-x-14 z-10 rounded-full hover:bg-muted/50 border border-border"
                              onClick={() => setCurrentNewsSlide(Math.max(0, currentNewsSlide - 1))}
                              disabled={currentNewsSlide === 0}
                            >
                              <ChevronLeft className="w-5 h-5 rtl-flip" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('portal.home.updates.next')}
                              className="absolute end-0 top-1/2 -translate-y-1/2 translate-x-14 [&:dir(rtl)]:-translate-x-14 z-10 rounded-full hover:bg-muted/50 border border-border"
                              onClick={() => setCurrentNewsSlide(Math.min(maxPage, currentNewsSlide + 1))}
                              disabled={currentNewsSlide >= maxPage}
                            >
                              <ChevronRight className="w-5 h-5 rtl-flip" />
                            </Button>

                            <div className="overflow-hidden">
                              <div
                                className="flex transition-transform duration-500 ease-in-out"
                                // Right to left the slides run leftwards from the
                                // start edge, so the strip moves the other way.
                                style={{ transform: `translateX(${direction === 'rtl' ? '' : '-'}${currentNewsSlide * 50}%)` }}
                              >
                                {allSlides.map((slide, idx) => (
                                  <div key={idx} className="w-1/2 flex-shrink-0 px-2">
                                    {slide.type === 'training' ? (
                                      /* ── Training Card: action-first with progress ──
                                         FIX (2026-05-14, ticket 696c6b46 + 9b36b6ae):
                                         body text + progress bar + CTA copy now ALL
                                         derive from the same `status` field so the badge
                                         can never disagree with the body. Real progress
                                         comes from enrollment.progress (was hardcoded 35).
                                         FIX (2026-05-14, ticket 099475cd): thumbnail
                                         rendered as <CourseThumbnail> so a NULL/empty
                                         course.thumbnail_url shows a deterministic
                                         letter-avatar placeholder — no <img src="">. */
                                      (() => {
                                        const { bodyText, progressValue } = trainingProgressBody(t, slide.status, slide.progress);
                                        const ctaCopy = trainingCtaCopy(t, slide.status);
                                        return (
                                      <Card className="group border border-border rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 transition-all duration-300 h-[320px] flex flex-col py-0 gap-0">
                                        <div className="relative h-[130px] overflow-hidden shrink-0">
                                          <CourseThumbnail
                                            src={slide.image}
                                            courseName={slide.title}
                                            className="group-hover:scale-105 transition-transform duration-500"
                                          />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
                                          <Badge className="absolute top-3 start-3 rounded-full text-[10px] bg-blue-600/90 text-white border-0 gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                                            {t('portal.home.updates.training')}
                                          </Badge>
                                          <Badge className={cn(
                                            "absolute top-3 end-3 rounded-full text-[10px] border-0",
                                            slide.status === 'Completed'
                                              ? 'bg-green-600/90 text-white'
                                              : slide.status === 'In Progress'
                                              ? 'bg-amber-500/90 text-white'
                                              : slide.status === 'Overdue'
                                              ? 'bg-red-600/90 text-white'
                                              : 'bg-white/20 text-white backdrop-blur-sm'
                                          )}>
                                            {enrollmentStatusLabel(t, slide.status)}
                                          </Badge>
                                        </div>
                                        <CardContent className="p-4 flex flex-col flex-1 justify-between">
                                          <div className="space-y-2.5">
                                            <h3 className="text-sm font-semibold text-foreground line-clamp-1" dir="auto">{slide.title}</h3>
                                            <div className="space-y-1.5">
                                              <div className="flex items-center justify-between text-[11px]">
                                                <span className="text-muted-foreground">{bodyText}</span>
                                                {dueLabel(slide) ? (
                                                  <span className={cn(
                                                    "font-medium",
                                                    (daysUntilDue(slide) ?? Infinity) <= 3
                                                      ? "text-red-500 dark:text-red-400"
                                                      : "text-muted-foreground"
                                                  )}>
                                                    {t('portal.home.due', { date: dueLabel(slide) })}
                                                  </span>
                                                ) : null}
                                              </div>
                                              <Progress value={progressValue} className="h-1.5" />
                                            </div>
                                          </div>
                                          {/* FIX #682: wire onClick to navigate to the SCORM player.
                                              enrollmentId is populated by transformToTrainingSlides
                                              when called from the live portal (via portalEnrollmentToLike).
                                              Preview slides (hardcoded arrays in theme-builder) have no
                                              enrollmentId — the button stays inert there, which is correct
                                              for a visual preview that cannot launch a real course. */}
                                          {/* #682 — visible-disabled state when enrollmentId is missing
                                              (preview-only mode, or recommendation card with no enrollment yet).
                                              Without disabled, the button looks live but does nothing on click. */}
                                          <Button
                                            size="sm"
                                            className="w-full rounded-lg mt-2 font-medium text-xs h-9"
                                            disabled={!slide.enrollmentId}
                                            title={
                                              slide.enrollmentId
                                                ? undefined
                                                : t('portal.home.updates.previewOnly')
                                            }
                                            onClick={
                                              slide.enrollmentId
                                                ? () => { router.push(`/portal/training/${slide.enrollmentId}`); }
                                                : undefined
                                            }
                                          >
                                            {t('portal.home.updates.cta', { label: ctaCopy })}
                                          </Button>
                                        </CardContent>
                                      </Card>
                                        );
                                      })()
                                    ) : (
                                      /* ── News / Policy Card: same visual-first, scannable design ── */
                                      <Card onClick={() => openUpdate(slide)} className="group border border-border rounded-2xl overflow-hidden cursor-pointer hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 transition-all duration-300 h-[320px] flex flex-col py-0 gap-0">
                                        <div className="relative h-[140px] overflow-hidden shrink-0">
                                          {/* Only render <img> when there's a real image URL.
                                              Empty strings produced `<img src="">` which the
                                              browser resolved to the page URL, then leaked
                                              the alt text (article title) as visible text
                                              under the gradient — extremely ugly. */}
                                          {slide.image ? (
                                            <img
                                              src={resolveAssetUrl(slide.image)}
                                              alt={slide.title}
                                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                              onError={(e) => {
                                                const img = e.currentTarget
                                                img.style.display = 'none'
                                                const fallback = img.nextElementSibling as HTMLElement | null
                                                if (fallback) fallback.style.display = 'flex'
                                              }}
                                            />
                                          ) : null}
                                          <div
                                            className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/10 items-center justify-center"
                                            style={{ display: slide.image ? 'none' : 'flex' }}
                                          >
                                            <BookOpen className="w-8 h-8 text-primary/50" />
                                          </div>
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                          <Badge variant="outline" className="absolute top-3 start-3 rounded-full text-[10px] px-2 bg-white/10 backdrop-blur-sm text-white border-white/20">
                                            {newsCategoryLabel(slide.category)}
                                          </Badge>
                                        </div>
                                        <CardContent className="p-4 flex flex-col flex-1 justify-between">
                                          <div className="space-y-2">
                                            {/* L-14: every card used to say "2 min read · Today",
                                                whatever the article and whenever it was published. */}
                                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                              <span>{t('portal.home.updates.readTime', { minutes: readingMinutes(slide.description) })}</span>
                                              {newsDate(slide) ? (
                                                <>
                                                  <span>·</span>
                                                  <span>{newsDate(slide)}</span>
                                                </>
                                              ) : null}
                                            </div>
                                            <h3 className="text-sm font-semibold text-foreground line-clamp-2 leading-snug" dir="auto">{slide.title}</h3>
                                            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2" dir="auto">{htmlToPlainText(slide.description)}</p>
                                          </div>
                                          <Button
                                            variant="link"
                                            className="text-primary p-0 h-auto text-xs font-medium justify-start"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openUpdate(slide);
                                            }}
                                          >
                                            {t('portal.home.updates.readMore')}
                                          </Button>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Dot indicators */}
                            {allSlides.length > 2 && (
                              <div className="flex items-center justify-center gap-1.5 mt-5">
                                {Array.from({ length: maxPage + 1 }).map((_, i) => (
                                  <button
                                    key={i}
                                    aria-label={t('portal.home.updates.page', { page: i + 1 })}
                                    className={cn(
                                      "h-1.5 rounded-full transition-all duration-300",
                                      i === currentNewsSlide ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                                    )}
                                    onClick={() => setCurrentNewsSlide(i)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    )}

                    {/* Progress & Accomplishments */}
                    {showProgressSection && (
                    <div className="py-12 px-8 bg-muted/50 rounded-2xl">
                      <div className="flex items-start gap-3 mb-8">
                        <h2 className="page-heading text-foreground">{t('portal.home.progress.title')}</h2>
                        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-500/10 rounded-full mt-1">
                          <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">i</span>
                          </div>
                          <span className="text-sm text-blue-600 dark:text-blue-400">{t('portal.home.progress.dataDelay')}</span>
                        </div>
                      </div>

                      {/* Stats Grid — Points/Badges suppressed when the Stats Bar
                          already shows them (#306), so the wrapper must account for
                          that to avoid rendering an empty grid. */}
                      {(showYourRank || showCertificates || ((showPoints || showBadges) && !showStatsBar)) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {showYourRank && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full bg-purple-50 dark:bg-purple-500/15 mx-auto mb-4 flex items-center justify-center">
                              <svg className="w-8 h-8 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                              </svg>
                            </div>
                            <div className="text-4xl font-bold text-foreground mb-2">{employeeRank}</div>
                            <div className="text-sm text-muted-foreground uppercase tracking-wide">{t('portal.home.progress.yourRank')}</div>
                          </CardContent>
                        </Card>
                        )}
                        {/* #306: Points + Badges are already shown by the Stats Bar
                            above. When the Stats Bar is enabled, omit them here so
                            the Progress & Accomplishments grid doesn't duplicate the
                            same counts — it keeps Rank + Certificates (metrics the
                            Stats Bar does NOT carry). When the Stats Bar is hidden,
                            the full four-card grid is retained so nothing is lost. */}
                        {showPoints && !showStatsBar && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-500/15 mx-auto mb-4 flex items-center justify-center">
                              <svg className="w-8 h-8 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="text-4xl font-bold text-foreground mb-2">{fmt.number(employeeTotalPoints)}</div>
                            <div className="text-sm text-muted-foreground uppercase tracking-wide">{t('portal.home.progress.points')}</div>
                          </CardContent>
                        </Card>
                        )}
                        {showBadges && !showStatsBar && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-500/15 mx-auto mb-4 flex items-center justify-center">
                              <svg className="w-8 h-8 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                              </svg>
                            </div>
                            <div className="text-4xl font-bold text-foreground mb-2">{employeeBadgeCount}</div>
                            <div className="text-sm text-muted-foreground uppercase tracking-wide">{t('portal.home.progress.badges')}</div>
                          </CardContent>
                        </Card>
                        )}
                        {showCertificates && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-500/15 mx-auto mb-4 flex items-center justify-center">
                              <svg className="w-8 h-8 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="text-4xl font-bold text-foreground mb-2">0</div>
                            <div className="text-sm text-muted-foreground uppercase tracking-wide">{t('portal.home.progress.certificates')}</div>
                          </CardContent>
                        </Card>
                        )}
                      </div>
                      )}

                      {/* Risk Score and Charts */}
                      {(showRiskScore || showCompromised || showAcknowledged || showEvaluated) && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Risk Score */}
                        {showRiskScore && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-8">
                            <h3 className="text-xl font-bold text-foreground mb-8">{t('portal.home.risk.title')}</h3>
                            <div className="flex flex-col items-center">
                              <div className="relative w-80 h-48 mb-8">
                                <svg viewBox="0 0 320 160" className="w-full h-full">
                                  <defs>
                                    <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                      <stop offset="0%" stopColor="#10b981" />
                                      <stop offset="20%" stopColor="#84cc16" />
                                      <stop offset="40%" stopColor="#eab308" />
                                      <stop offset="60%" stopColor="#f97316" />
                                      <stop offset="80%" stopColor="#ef4444" />
                                      <stop offset="100%" stopColor="#dc2626" />
                                    </linearGradient>
                                  </defs>
                                  <path d="M 40 140 A 120 120 0 0 1 280 140" fill="none" stroke="url(#gaugeGradient)" strokeWidth="32" strokeLinecap="round" />
                                  <line x1="160" y1="140" x2="160" y2="40" stroke="currentColor" strokeWidth="3" transform={`rotate(${(employeeRiskScore / 100) * 180 - 90} 160 140)`} strokeLinecap="round" className="text-foreground" />
                                  <circle cx="160" cy="140" r="8" fill="currentColor" className="text-foreground" />
                                  {/* Tickets 898 / 1166: show the coarse band LABEL, never a
                                      precise-looking number — the portal has no real 0-100 score. */}
                                  <text x="160" y="120" textAnchor="middle" className="text-3xl font-bold fill-amber-600 dark:fill-amber-400">{employeeRiskLevelLabel ? riskLevelLabel(t, employeeRiskLevelLabel) : '—'}</text>
                                </svg>
                              </div>
                              <div className="relative w-64 h-48">
                                <svg viewBox="0 0 256 192" className="w-full h-full">
                                  <polygon points="128,20 220,160 36,160" fill="none" className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="2" />
                                  <polygon points="128,60 180,140 76,140" className="fill-blue-300/30 dark:fill-blue-400/20 stroke-blue-500 dark:stroke-blue-400" strokeWidth="2" />
                                  <text x="128" y="15" textAnchor="middle" className="text-xs fill-gray-500 dark:fill-gray-400">{t('portal.home.risk.behavior')}</text>
                                  <text x="30" y="175" textAnchor="middle" className="text-xs fill-gray-500 dark:fill-gray-400">{t('portal.home.risk.amplifier')}</text>
                                  <text x="226" y="175" textAnchor="middle" className="text-xs fill-gray-500 dark:fill-gray-400">{t('portal.home.risk.training')}</text>
                                </svg>
                              </div>
                              <p className="text-sm text-muted-foreground mt-4">{t('portal.home.risk.factors')}</p>
                            </div>
                          </CardContent>
                        </Card>
                        )}

                        {/* Compromised */}
                        {showCompromised && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-8">
                            <div className="flex items-center justify-between mb-8">
                              <h3 className="text-xl font-bold text-foreground">{t('portal.home.compromised.title')}</h3>
                              {/* Info-icon tooltip wired 2026-05-14 (ticket
                                  #204 — these icons were styled as cursor-help
                                  but had no actual help content; clicking
                                  did nothing). Native `title` shows on hover
                                  for sighted users; aria-label + role="img"
                                  surface the same copy to screen readers. */}
                              <div
                                className="w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center cursor-help"
                                role="img"
                                aria-label={t('portal.home.compromised.help')}
                                title={t('portal.home.compromised.help')}
                              ><span className="text-xs text-muted-foreground">i</span></div>
                            </div>
                            {(() => {
                              // BUG-513 — use the real per-recipient funnel
                              // when supplied (View-As). The synthetic branch
                              // only runs for the recipient-less settings
                              // theme preview.
                              // Tickets 990/999 — no synthetic floor; an empty
                              // history renders honest zeros (was Math.max(..,3)).
                              const ps = props.phishingStats;
                              const sent = ps ? ps.sent : employeeTimesCompromised * 3;
                              const opened = ps ? ps.opened : Math.round(sent * 0.78);
                              const clicked = ps ? ps.clicked : employeeTimesCompromised;
                              const compromised = ps ? ps.compromised : employeeTimesCompromised;
                              const reported = ps ? ps.reported : Math.round(sent * (employeeReportRate / 100));
                              const trained = ps ? ps.trained : Math.max(Math.round(sent * (employeeTrainingCompletion / 100)), compromised > 0 ? 1 : 0);
                              const evaluated = ps ? ps.evaluated : trained;
                              const compromisedPercent = sent > 0 ? ((compromised / sent) * 100) : 0;
                              const compromisedDashoffset = 251.2 - (251.2 * compromisedPercent / 100);
                              return (
                              <>
                            <div className="flex items-center justify-between gap-4 mb-8">
                              {/* FIX 2026-05-12: side meta now driven by GET /v1/portal/benchmarks
                                  (PortalBenchmarksController). Renders empty when benchmarks prop absent
                                  (real employee portal — no admin JWT to call the endpoint). */}
                              <div className="flex-1 space-y-3">
                                {props.benchmarks && (() => {
                                  const teamAvg = props.benchmarks.compromised.teamAvg;
                                  const lastIncident = props.benchmarks.compromised.lastIncident;
                                  const delta = Math.round(compromisedPercent - teamAvg);
                                  const onTrack = compromisedPercent <= teamAvg;
                                  return (
                                    <>
                                      <div>
                                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.benchmark.vsTeamAvg')}</div>
                                        <div className="flex items-baseline gap-2">
                                          <span className="text-sm font-semibold text-foreground">{t('portal.home.benchmark.you', { percent: compromisedPercent.toFixed(0) })}</span>
                                          <span className="text-xs text-muted-foreground">{t('portal.home.benchmark.team', { percent: teamAvg.toFixed(0) })}</span>
                                          <span className={cn('text-xs font-medium', onTrack ? 'text-green-500' : 'text-orange-400')}>
                                            {delta >= 0 ? '+' : ''}{delta}%
                                          </span>
                                        </div>
                                      </div>
                                      {lastIncident && (
                                        <div>
                                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.benchmark.lastIncident')}</div>
                                          <div className="text-sm text-foreground" dir="auto">{lastIncident.name}</div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="relative w-24 h-24 shrink-0">
                                <svg className="w-full h-full transform -rotate-90">
                                  <circle cx="48" cy="48" r="40" fill="none" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="8" />
                                  <circle cx="48" cy="48" r="40" fill="none" className="stroke-red-400 dark:stroke-red-400/80" strokeWidth="8" strokeDasharray="251.2" strokeDashoffset={compromisedDashoffset} strokeLinecap="round" />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center"><span className="text-sm font-bold text-foreground">{compromisedPercent.toFixed(2)}%</span></div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              {[
                                { label: t('portal.home.funnel.sent'), value: sent, percent: 100, color: 'bg-blue-500' },
                                { label: t('portal.home.funnel.opened'), value: opened, percent: sent > 0 ? Math.round((opened / sent) * 100) : 0, color: 'bg-orange-400' },
                                { label: t('portal.home.funnel.clicked'), value: clicked, percent: sent > 0 ? Math.round((clicked / sent) * 100) : 0, color: 'bg-red-300' },
                                { label: t('portal.home.funnel.compromised'), value: compromised, percent: sent > 0 ? Math.round((compromised / sent) * 100) : 0, color: 'bg-red-400' },
                                { label: t('portal.home.funnel.reported'), value: reported, percent: sent > 0 ? Math.round((reported / sent) * 100) : 0, color: reported > 0 ? 'bg-green-400' : 'bg-gray-300' },
                                { label: t('portal.home.funnel.trained'), value: trained, percent: sent > 0 ? Math.round((trained / sent) * 100) : 0, color: 'bg-green-400' },
                                { label: t('portal.home.funnel.evaluated'), value: evaluated, percent: sent > 0 ? Math.round((evaluated / sent) * 100) : 0, color: 'bg-green-400' },
                              ].map((item, i) => (
                                <div key={i} className="space-y-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground min-w-[100px]">{item.label}</span>
                                    <div className="flex items-center gap-3 flex-1">
                                      <span className="text-foreground font-medium w-6 text-end">{item.value}</span>
                                      <div className="flex-1 bg-muted/50 rounded-full h-2 overflow-hidden">
                                        <div className={`h-full ${item.color} transition-all`} style={{ width: `${item.percent}%` }} />
                                      </div>
                                      <span className="text-foreground font-medium w-10 text-end">{item.percent}%</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                              </>
                              );
                            })()}
                          </CardContent>
                        </Card>
                        )}

                        {/* Acknowledged */}
                        {showAcknowledged && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-8">
                            <div className="flex items-center justify-between mb-8">
                              <h3 className="text-xl font-bold text-foreground">{t('portal.home.acknowledged.title')}</h3>
                              <div
                                className="w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center cursor-help"
                                role="img"
                                aria-label={t('portal.home.acknowledged.help')}
                                title={t('portal.home.acknowledged.help')}
                              ><span className="text-xs text-muted-foreground">i</span></div>
                            </div>
                            {(() => {
                              // Ticket 890 — when a real per-recipient funnel is
                              // supplied (View-As), the Acknowledged card MUST
                              // read the same Sent/Opened/Clicked as the
                              // Compromised card instead of an independent
                              // synthetic formula, so the sibling cards agree.
                              // The synthetic branch only runs for the
                              // recipient-less settings theme preview.
                              // Tickets 990/999 — NO synthetic floor: an empty
                              // history (employeeTimesCompromised === 0) must
                              // render honest zeros, never a phantom
                              // Sent 3 / Opened 3 / Clicked 1 / Acknowledged 1.
                              const ps = props.phishingStats;
                              // Sent/Opened/Clicked MUST match the Compromised
                              // card's funnel — same source when ps is present,
                              // and the SAME synthetic formula (opened = round(
                              // sent*0.78), clicked = timesCompromised) when it
                              // is absent — so the sibling cards never disagree.
                              const ackSent = ps ? ps.sent : employeeTimesCompromised * 3;
                              const ackOpened = ps ? ps.opened : Math.round(ackSent * 0.78);
                              const ackClicked = ps ? ps.clicked : employeeTimesCompromised;
                              const ackAcknowledged = ps ? ps.reported : Math.round(ackSent * (employeeReportRate / 100));
                              const ackTrained = ps ? ps.trained : Math.round(ackSent * (employeeTrainingCompletion / 100));
                              const ackEvaluated = ps ? ps.evaluated : ackTrained;
                              const ackPercent = ackSent > 0 ? ((ackAcknowledged / ackSent) * 100) : 0;
                              const ackDashoffset = 251.2 - (251.2 * ackPercent / 100);
                              return (
                              <>
                            <div className="flex items-end justify-between gap-4 mb-8">
                              {/* FIX 2026-05-12: side meta driven by GET /v1/portal/benchmarks. */}
                              <div className="flex-1 space-y-3">
                                {props.benchmarks && (() => {
                                  const teamAvg = props.benchmarks.acknowledged.teamAvg;
                                  const lastActivity = props.benchmarks.acknowledged.lastActivity;
                                  const delta = Math.round(ackPercent - teamAvg);
                                  const onTrack = ackPercent >= teamAvg;
                                  return (
                                    <>
                                      <div>
                                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.benchmark.vsTeamAvg')}</div>
                                        <div className="flex items-baseline gap-2">
                                          <span className="text-sm font-semibold text-foreground">{t('portal.home.benchmark.you', { percent: ackPercent.toFixed(0) })}</span>
                                          <span className="text-xs text-muted-foreground">{t('portal.home.benchmark.team', { percent: teamAvg.toFixed(0) })}</span>
                                          <span className={cn('text-xs font-medium', onTrack ? 'text-green-500' : 'text-orange-400')}>
                                            {delta >= 0 ? '+' : ''}{delta}%
                                          </span>
                                        </div>
                                      </div>
                                      {lastActivity && (
                                        <div>
                                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.benchmark.lastActivity')}</div>
                                          <div className="text-sm text-foreground" dir="auto">{lastActivity.name}</div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="relative w-24 h-24 shrink-0">
                                <svg className="w-full h-full transform -rotate-90">
                                  <circle cx="48" cy="48" r="40" fill="none" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="8" />
                                  <circle cx="48" cy="48" r="40" fill="none" className="stroke-green-300 dark:stroke-green-400/80" strokeWidth="8" strokeDasharray="251.2" strokeDashoffset={ackDashoffset} strokeLinecap="round" />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center"><span className="text-sm font-bold text-foreground">{ackPercent.toFixed(2)}%</span></div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              {[
                                { label: t('portal.home.funnel.sent'), value: ackSent, percent: 100, color: 'bg-blue-400' },
                                { label: t('portal.home.funnel.opened'), value: ackOpened, percent: ackSent > 0 ? Math.round((ackOpened / ackSent) * 100) : 0, color: 'bg-orange-400' },
                                { label: t('portal.home.funnel.clicked'), value: ackClicked, percent: ackSent > 0 ? Math.round((ackClicked / ackSent) * 100) : 0, color: 'bg-green-400' },
                                { label: t('portal.home.funnel.acknowledged'), value: ackAcknowledged, percent: ackSent > 0 ? Math.round((ackAcknowledged / ackSent) * 100) : 0, color: 'bg-green-400' },
                                { label: t('portal.home.funnel.trained'), value: ackTrained, percent: ackSent > 0 ? Math.round((ackTrained / ackSent) * 100) : 0, color: ackTrained > 0 ? 'bg-green-400' : 'bg-gray-300' },
                                { label: t('portal.home.funnel.evaluated'), value: ackEvaluated, percent: ackSent > 0 ? Math.round((ackEvaluated / ackSent) * 100) : 0, color: ackEvaluated > 0 ? 'bg-green-400' : 'bg-gray-300' },
                              ].map((item, i) => (
                                <div key={i} className="space-y-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground min-w-[120px]">{item.label}</span>
                                    <div className="flex items-center gap-3 flex-1">
                                      <span className="text-foreground font-medium w-6 text-end">{item.value}</span>
                                      <div className="flex-1 bg-muted/50 rounded-full h-2 overflow-hidden">
                                        <div className={`h-full ${item.color} transition-all`} style={{ width: `${item.percent}%` }} />
                                      </div>
                                      <span className="text-foreground font-medium w-10 text-end">{item.percent}%</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                              </>
                              );
                            })()}
                          </CardContent>
                        </Card>
                        )}

                        {/* Evaluated */}
                        {showEvaluated && (
                        <Card className="border-border rounded-xl cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                          <CardContent className="p-8">
                            <div className="flex items-center justify-between mb-8">
                              <h3 className="text-xl font-bold text-foreground">{t('portal.home.evaluated.title')}</h3>
                              <div
                                className="w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center cursor-help"
                                role="img"
                                aria-label={t('portal.home.evaluated.help')}
                                title={t('portal.home.evaluated.help')}
                              ><span className="text-xs text-muted-foreground">i</span></div>
                            </div>
                            {(() => {
                              // FIX b8168b19 (2026-05-12): card values derived from the
                              // same `props.courseCards` the Trainings tab below renders,
                              // so the "14 Assigned" header can never contradict an empty
                              // tab. STATUS_LABEL produces 'Completed' / 'In Progress' /
                              // 'Not Started' / 'Overdue' — mapped here as:
                              //   Assigned = total
                              //   Trained  = Completed  (Evaluated mirrors Trained, matching the
                              //              prior visual contract where both rows showed the same %)
                              //   Pending  = In Progress + Not Started  (not done, not expired)
                              //   Expired  = Overdue
                              const realCourses = props.courseCards ?? [];
                              const evalAssigned = realCourses.length;
                              const evalTrained = realCourses.filter(c => c.status === 'Completed').length;
                              const evalEvaluated = evalTrained;
                              const evalPending = realCourses.filter(c => c.status === 'In Progress' || c.status === 'Not Started').length;
                              const evalExpired = realCourses.filter(c => c.status === 'Overdue').length;
                              const evalCompletionRate = evalAssigned > 0 ? ((evalEvaluated / evalAssigned) * 100) : 0;
                              const evalDashoffset = 251.2 - (251.2 * evalCompletionRate / 100);
                              return (
                              <>
                            <div className="flex items-end justify-between gap-4 mb-8">
                              {/* FIX 2026-05-12: teamAvg driven by GET /v1/portal/benchmarks.
                                  Next deadline derived FE-side from props.courseCards — filter to non-Completed
                                  rows with a dueDay (the workspace's calendar day), sort by days left, take
                                  first. Both halves render only when data is actually present (no fake
                                  fallbacks). */}
                              <div className="flex-1 space-y-3">
                                {props.benchmarks && (() => {
                                  const teamAvg = props.benchmarks.evaluated.teamAvg;
                                  const delta = Math.round(evalCompletionRate - teamAvg);
                                  const onTrack = evalCompletionRate >= teamAvg;
                                  return (
                                    <div>
                                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.benchmark.vsTeamAvg')}</div>
                                      <div className="flex items-baseline gap-2">
                                        <span className="text-sm font-semibold text-foreground">{t('portal.home.benchmark.you', { percent: evalCompletionRate.toFixed(0) })}</span>
                                        <span className="text-xs text-muted-foreground">{t('portal.home.benchmark.team', { percent: teamAvg.toFixed(0) })}</span>
                                        <span className={cn('text-xs font-medium', onTrack ? 'text-green-500' : 'text-orange-400')}>
                                          {delta >= 0 ? '+' : ''}{delta}%
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {(() => {
                                  // Next deadline: the user's next-due unfinished training, derived
                                  // from props.courseCards (transformToCourseCards hands over the
                                  // workspace's calendar day, dueDay; '' = no deadline). Render only
                                  // when at least one row has one. Days are counted in the zone the
                                  // day is in (dueTimeZone), as the API counts them.
                                  const incomplete = (props.courseCards ?? []).filter(
                                    (c) => c.status !== 'Completed' && !!c.dueDay
                                  );
                                  if (incomplete.length === 0) return null;
                                  const next = [...incomplete].sort((a, b) => {
                                    const da = daysUntilDue(a);
                                    const db2 = daysUntilDue(b);
                                    if (da === null && db2 === null) return 0;
                                    if (da === null) return 1;
                                    if (db2 === null) return -1;
                                    return da - db2;
                                  })[0];
                                  const daysUntil = daysUntilDue(next);
                                  return (
                                    <div>
                                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.benchmark.nextDeadline')}</div>
                                      <div className="text-sm text-foreground" dir="auto">{next.title}</div>
                                      {daysUntil != null && (
                                        <div className="text-xs text-muted-foreground">
                                          {daysUntil > 0
                                            ? t('portal.home.benchmark.dueInDays', { days: daysUntil })
                                            : daysUntil === 0
                                              ? t('portal.home.benchmark.dueToday')
                                              : t('portal.home.benchmark.overdueByDays', { days: Math.abs(daysUntil) })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="relative w-24 h-24 shrink-0">
                                <svg className="w-full h-full transform -rotate-90">
                                  <circle cx="48" cy="48" r="40" fill="none" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="8" />
                                  <circle cx="48" cy="48" r="40" fill="none" className="stroke-green-300 dark:stroke-green-400/80" strokeWidth="8" strokeDasharray="251.2" strokeDashoffset={evalDashoffset} strokeLinecap="round" />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center"><span className="text-sm font-bold text-foreground">{evalCompletionRate.toFixed(2)}%</span></div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              {[
                                { label: t('portal.home.funnel.assigned'), value: evalAssigned, percent: 100, color: 'bg-blue-400' },
                                { label: t('portal.home.funnel.trained'), value: evalTrained, percent: evalAssigned > 0 ? Math.round((evalTrained / evalAssigned) * 100) : 0, color: 'bg-green-400' },
                                { label: t('portal.home.funnel.evaluated'), value: evalEvaluated, percent: evalAssigned > 0 ? Math.round((evalEvaluated / evalAssigned) * 100) : 0, color: 'bg-green-400' },
                                { label: t('portal.home.funnel.pending'), value: evalPending, percent: evalAssigned > 0 ? Math.round((evalPending / evalAssigned) * 100) : 0, color: 'bg-orange-400' },
                                { label: t('portal.home.funnel.expired'), value: evalExpired, percent: evalAssigned > 0 ? Math.round((evalExpired / evalAssigned) * 100) : 0, color: 'bg-red-300' },
                                { label: t('portal.home.funnel.assignedUnique'), value: evalAssigned, percent: 100, color: 'bg-red-300' },
                                { label: t('portal.home.funnel.completedUnique'), value: evalEvaluated, percent: evalAssigned > 0 ? Math.round((evalEvaluated / evalAssigned) * 100) : 0, color: 'bg-green-400' },
                              ].map((item, i) => (
                                <div key={i} className="space-y-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground min-w-[120px]">{item.label}</span>
                                    <div className="flex items-center gap-3 flex-1">
                                      <span className="text-foreground font-medium w-6 text-end">{item.value}</span>
                                      <div className="flex-1 bg-muted/50 rounded-full h-2 overflow-hidden">
                                        <div className={`h-full ${item.color} transition-all`} style={{ width: `${item.percent}%` }} />
                                      </div>
                                      <span className="text-foreground font-medium w-10 text-end">{item.percent}%</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                              </>
                              );
                            })()}
                          </CardContent>
                        </Card>
                        )}
                      </div>
                      )}
                    </div>
                    )}

                    {/* Challenges — the employee portal only (item 3) */}
                    {props.challengesSection}

                    {/* Courses Section */}
                    {anyTabVisible && (
                    <div id="portal-section-tabs" className="py-12 px-8 bg-background">
                      <h2 className="page-heading text-foreground mb-8">{tabNames[activePortalTab]}</h2>

                      <div className="flex items-center justify-between mb-8">
                        {/* <nav> landmark for the in-page section navigation.
                            The child tablist keeps the tab-widget semantics
                            (roving tabindex + arrow-key handling); the nav
                            wrapper provides the screen-reader navigation region. */}
                        <nav aria-label={t('portal.home.tabs.navigation')}>
                        <div
                          role="tablist"
                          aria-label={t('portal.home.tabs.list')}
                          className="flex items-center gap-1 bg-muted/50 rounded-lg p-1"
                          onKeyDown={(e) => {
                            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
                            const visibleIdx = [0, 1, 2, 3, 4].filter((i) => tabVisibility[i]);
                            if (visibleIdx.length === 0) return;
                            const currentPos = Math.max(0, visibleIdx.indexOf(activePortalTab));
                            let next = currentPos;
                            // The arrow that points to the NEXT tab: left when
                            // the tabs run right to left (WAI-ARIA tabs pattern).
                            const forward = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
                            if (e.key === forward) next = (currentPos + 1) % visibleIdx.length;
                            else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') next = (currentPos - 1 + visibleIdx.length) % visibleIdx.length;
                            if (e.key === 'Home') next = 0;
                            if (e.key === 'End') next = visibleIdx.length - 1;
                            e.preventDefault();
                            handleTabChange(visibleIdx[next]);
                          }}
                        >
                          {tabNames
                            .map((tab, idx) => ({ tab, idx }))
                            .filter(({ idx }) => tabVisibility[idx])
                            .map(({ tab, idx }) => {
                              const isActive = activePortalTab === idx;
                              return (
                                <Button
                                  key={idx}
                                  role="tab"
                                  id={`portal-tab-${idx}`}
                                  aria-selected={isActive}
                                  aria-controls={`portal-tabpanel-${idx}`}
                                  tabIndex={isActive ? 0 : -1}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTabChange(idx)}
                                  className={cn(
                                    'rounded-md transition-colors',
                                    isActive
                                      ? 'bg-foreground text-background hover:bg-foreground/90 hover:text-background'
                                      : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                                  )}
                                >
                                  {tab}
                                </Button>
                              );
                            })}
                        </div>
                        </nav>
                        <div className="flex items-center gap-2">
                          {/* Grid/List toggle — only for tabs that support it */}
                          {tabHasGridView[activePortalTab] && (
                            <>
                              <Button
                                variant="outline"
                                size="icon"
                                aria-label={t('portal.home.view.grid')}
                                className={cn("rounded-md", viewMode === 'grid' && "bg-foreground! text-background! border-transparent! hover:bg-foreground/90! hover:text-background!")}
                                onClick={() => setViewMode('grid')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                aria-label={t('portal.home.view.list')}
                                className={cn("rounded-md", viewMode === 'list' && "bg-foreground! text-background! border-transparent! hover:bg-foreground/90! hover:text-background!")}
                                onClick={() => setViewMode('list')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                              </Button>
                            </>
                          )}
                          {/* Filter dropdown — tab-specific options */}
                          {(tabFilterOptions[activePortalTab]?.length ?? 0) > 1 && (
                            <Select value={filterValue} onValueChange={setFilterValue}>
                              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {tabFilterOptions[activePortalTab].map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {/* Sort toggle */}
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-md"
                            title={sortOrder === 'asc' ? tabSortLabel[activePortalTab]?.[0] : tabSortLabel[activePortalTab]?.[1]}
                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                          >
                            <svg className={cn("w-4 h-4 transition-transform", sortOrder === 'desc' && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>
                          </Button>
                        </div>
                      </div>

                      <div
                        role="tabpanel"
                        id={`portal-tabpanel-${activePortalTab}`}
                        aria-labelledby={`portal-tab-${activePortalTab}`}
                      >
                      {activePortalTab === 0 && showTrainings && (() => {
                        // No hardcoded course fallbacks — empty array renders
                        // the existing "no courses" empty state instead of fake
                        // titles ("URL Training", "Social Engineering Basics").
                        const allCourses = props.courseCards ?? [];
                        const filtered = filterValue === 'all' ? allCourses :
                          allCourses.filter(c => c.status.toLowerCase().replace(/\s+/g, '-') === filterValue);
                        const sorted = [...filtered].sort((a, b) =>
                          sortOrder === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title)
                        );
                        // FIX (2026-05-14, ticket af89916d): paginate the rendered set so
                        // the "View More" button actually does something.
                        const visible = sorted.slice(0, trainingsVisibleCount);
                        const hasMore = sorted.length > visible.length;
                        return (
                          <>
                            {viewMode === 'grid' ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {visible.map((course, idx) => (
                                  <Card key={idx} className="group rounded-xl overflow-hidden border-border hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 transition-shadow duration-300 py-0 gap-0">
                                    {/* FIX (2026-05-14, ticket 099475cd): single
                                        <CourseThumbnail> handles NULL/empty src
                                        with a deterministic letter-avatar fallback
                                        — no <img src=""> ever ships. */}
                                    <div className="h-44 overflow-hidden relative">
                                      <CourseThumbnail
                                        src={course.image}
                                        courseName={course.title}
                                        className="group-hover:scale-105 transition-transform duration-300"
                                      />
                                    </div>
                                    <CardContent className="p-4 space-y-3">
                                      <h3 className="font-semibold text-foreground line-clamp-1" dir="auto">{course.title}</h3>
                                      <div className="flex items-center justify-between">
                                        <Badge variant="secondary" className={cn("rounded-full text-xs", course.statusColor)}>{enrollmentStatusLabel(t, course.status)}</Badge>
                                        <span className="text-xs text-muted-foreground">{t('portal.home.trainings.attempts', { count: course.attempts })}</span>
                                      </div>
                                      {/* FIX #895 (spec E6.1): show real progress % and due date. */}
                                      {typeof course.progress === 'number' && (
                                        <div>
                                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                            <span>{t('portal.home.trainings.progress')}</span>
                                            <span className="font-medium text-foreground">{Math.round(course.progress)}%</span>
                                          </div>
                                          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(100, Math.max(0, course.progress))}%` }} />
                                          </div>
                                        </div>
                                      )}
                                      {dueLabel(course) ? (
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                          <span>{t('portal.home.due', { date: dueLabel(course) })}</span>
                                        </div>
                                      ) : null}
                                      {/* FIX (2026-05-14, ticket 9b36b6ae): CTA was hardcoded
                                          "Start Training". Now branches on status so In Progress
                                          rows say "Continue Training" and Completed rows say
                                          "Review Training". */}
                                      {/* FIX #681: wire onClick to navigate to the SCORM player.
                                          Visible-disabled state when enrollmentId is missing so users see
                                          immediately that the button cannot launch (e.g. preview cards). */}
                                      <Button
                                        size="sm"
                                        className="w-full rounded-md"
                                        disabled={!course.enrollmentId}
                                        title={
                                          course.enrollmentId
                                            ? undefined
                                            : t('portal.home.trainings.noEnrollment')
                                        }
                                        onClick={
                                          course.enrollmentId
                                            ? () => { router.push(`/portal/training/${course.enrollmentId}`); }
                                            : undefined
                                        }
                                      >{trainingCtaCopy(t, course.status)}</Button>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            ) : (
                              <div className="space-y-3 mb-8">
                                {visible.map((course, idx) => (
                                  <Card key={idx} className="rounded-xl border-border hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 transition-shadow duration-300 py-0 gap-0">
                                    <CardContent className="p-4 flex items-center gap-4">
                                      <CourseThumbnail
                                        src={course.image}
                                        courseName={course.title}
                                        className="w-20 h-14 rounded-lg shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-foreground line-clamp-1" dir="auto">{course.title}</h3>
                                        {/* FIX #895 (spec E6.1): supplement Attempts with progress % and due date. */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                          <span>{t('portal.home.trainings.attempts', { count: course.attempts })}</span>
                                          {typeof course.progress === 'number' && (
                                            <span className="font-medium text-foreground">{Math.round(course.progress)}%</span>
                                          )}
                                          {dueLabel(course) ? (
                                            <span className="inline-flex items-center gap-1">
                                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                              {t('portal.home.due', { date: dueLabel(course) })}
                                            </span>
                                          ) : null}
                                        </div>
                                      </div>
                                      <Badge variant="secondary" className={cn("rounded-full text-xs shrink-0", course.statusColor)}>{enrollmentStatusLabel(t, course.status)}</Badge>
                                      {/* FIX (2026-05-14, ticket 9b36b6ae): list-view CTA also branches on status. */}
                                      {/* FIX #681: list-view CTA with visible-disabled when enrollmentId missing. */}
                                      <Button
                                        size="sm"
                                        className="rounded-md shrink-0"
                                        disabled={!course.enrollmentId}
                                        title={
                                          course.enrollmentId
                                            ? undefined
                                            : t('portal.home.trainings.noEnrollment')
                                        }
                                        onClick={
                                          course.enrollmentId
                                            ? () => { router.push(`/portal/training/${course.enrollmentId}`); }
                                            : undefined
                                        }
                                      >{trainingCtaCopy(t, course.status)}</Button>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            )}
                            {sorted.length === 0 && <p className="text-center text-muted-foreground py-8">{t('portal.home.trainings.noMatch')}</p>}
                            {sorted.length > 0 && hasMore && (
                              <div className="flex justify-center">
                                {/* FIX (2026-05-14, ticket af89916d): wired to load
                                    the next page of cards. Was a dead button. */}
                                <Button
                                  variant="outline"
                                  className="px-8 rounded-md border-border"
                                  onClick={() => setTrainingsVisibleCount((n) => n + TRAININGS_PAGE_SIZE)}
                                >
                                  {t('portal.home.trainings.viewMore')}
                                </Button>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {activePortalTab === 1 && showLeaderboard && (() => {
                        // FIX (portal-manager-fix-wave-1, 2026-05-12): removed the
                        // ?? SAMPLE_LEADERBOARD fallback. Live employees must never
                        // see fake names. Empty array → renders empty state below.
                        const allEntries = props.leaderboardData ?? [];
                        const sorted = [...allEntries].sort((a, b) =>
                          sortOrder === 'asc' ? b.points - a.points : a.points - b.points
                        );
                        if (sorted.length === 0) {
                          return (
                            <Card className="rounded-xl border-border">
                              <CardContent className="p-10 text-center">
                                <p className="text-sm text-muted-foreground">
                                  {t('portal.home.leaderboard.empty')}
                                </p>
                              </CardContent>
                            </Card>
                          );
                        }
                        return (
                          <>
                            <div className="flex justify-center mb-4">
                              <LeaderboardScopeToggle
                                value={leaderboardScope}
                                onValueChange={setLeaderboardScope}
                              />
                            </div>
                          <Card className="rounded-xl border-border cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                            <CardContent className="p-6">
                              <div className="space-y-4">
                                {sorted.map((entry, idx) => (
                                  <div key={entry.name} className={cn(
                                    "flex items-center justify-between py-3 border-b last:border-0 -mx-2 px-2 rounded-lg",
                                    entry.isCurrentUser && "bg-primary/5 ring-1 ring-primary/20"
                                  )}>
                                    <div className="flex items-center gap-4">
                                      <span className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                                        idx === 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                                        idx === 1 ? "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" :
                                        idx === 2 ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" :
                                        "bg-muted text-muted-foreground"
                                      )}>{idx + 1}</span>
                                      <div>
                                        <p className="font-medium text-foreground"><bdi>{entry.name}</bdi>{entry.isCurrentUser && <span className="ms-2 text-xs font-normal text-primary">{t('portal.home.leaderboard.you')}</span>}</p>
                                        <p className="text-xs text-muted-foreground" dir="auto">{entry.dept}</p>
                                      </div>
                                    </div>
                                    <span className="font-bold text-foreground">{t('portal.home.pts', { points: fmt.number(entry.points) })}</span>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                          </>
                        );
                      })()}

                      {activePortalTab === 2 && showPointsTab && (() => {
                        // FIX 2026-05-12: removed `?? [hardcoded 5 fake activities]` fallback.
                        // Empty array → the existing "No points history matches the selected filter."
                        // empty state below handles it truthfully instead of leaking fake activity
                        // names ("Completed URL Training", "Reported phishing email", etc.) into
                        // a real customer's portal.
                        const allPoints = props.pointsHistory ?? [];
                        const filtered = filterValue === 'all' ? allPoints :
                          filterValue === 'earned' ? allPoints.filter(p => p.points.startsWith('+')) :
                          allPoints.filter(p => p.points.startsWith('-'));
                        const sorted = sortOrder === 'asc' ? filtered : [...filtered].reverse();
                        return (
                          <Card className="rounded-xl border-border cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]">
                            <CardContent className="p-6">
                              <div className="space-y-4">
                                <div className="text-center py-4">
                                  <div className="text-5xl font-bold text-foreground mb-2">{fmt.number(employeeTotalPoints)}</div>
                                  <p className="text-muted-foreground">{t('portal.home.pointsTab.total')}</p>
                                </div>
                                <Separator />
                                <div className="space-y-3">
                                  {sorted.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                                      <div>
                                        <p className="text-sm font-medium text-foreground" dir="auto">{item.activity}</p>
                                        {/* The award's instant, worded in the reader's language. */}
                                        <p className="text-xs text-muted-foreground">{portalTimeAgo(t, item.date)}</p>
                                      </div>
                                      <span className={cn("text-sm font-bold", item.points.startsWith('+') ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}>{item.points}</span>
                                    </div>
                                  ))}
                                  {sorted.length === 0 && <p className="text-center text-muted-foreground py-4">{t('portal.home.pointsTab.noMatch')}</p>}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {activePortalTab === 3 && showBadgesTab && (() => {
                        const allBadges = props.badgeCards ?? [];
                        if (allBadges.length === 0) {
                          return <p className="text-center text-muted-foreground py-12">{t('portal.home.badgesTab.none')}</p>;
                        }
                        const filtered = filterValue === 'all' ? allBadges :
                          filterValue === 'earned' ? allBadges.filter(b => b.earned) :
                          allBadges.filter(b => !b.earned);
                        const sorted = [...filtered].sort((a, b) =>
                          sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
                        );
                        return viewMode === 'grid' ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {sorted.map((badge, idx) => (
                              <Card key={idx} onClick={() => setSelectedBadge(badge)} className={cn(
                                "p-4 rounded-xl transition-all cursor-pointer hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01]",
                                badge.earned ? "bg-card border-green-200 dark:border-green-900/50" : "bg-muted/50 border-border opacity-60"
                              )}>
                                <div className="flex flex-col items-center text-center gap-2">
                                  <div className="relative">
                                    {badge.earned ? (
                                      <>
                                        {badge.icon && badge.icon.trim() ? (
                                          // eslint-disable-next-line @next/next/no-img-element
                                          <img src={badge.icon} alt={badge.name} className="w-16 h-16 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex'); }} />
                                        ) : null}
                                        {(!badge.icon || !badge.icon.trim()) && (
                                          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                                            <Award className="w-8 h-8 text-amber-500" />
                                          </div>
                                        )}
                                        {badge.icon && badge.icon.trim() && (
                                          <div className="w-16 h-16 rounded-full bg-muted items-center justify-center hidden">
                                            <Award className="w-8 h-8 text-amber-500" />
                                          </div>
                                        )}
                                        <CheckCircle2 className="w-5 h-5 text-green-500 absolute -top-1 -end-1 bg-background rounded-full" />
                                      </>
                                    ) : (
                                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                                        <Lock className="w-6 h-6 text-muted-foreground" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-full">
                                    <h4 className="font-semibold text-sm text-foreground" dir="auto">{badge.name}</h4>
                                    <div className="mt-2 space-y-1">
                                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                        {badge.earned ? t('portal.home.badge.earnedFor') : t('portal.home.badge.howToEarn')}
                                      </div>
                                      <p className="text-xs text-foreground/80 leading-snug" dir="auto">
                                        {badge.description || t('portal.home.badge.defaultDescription')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))}
                            {sorted.length === 0 && <p className="col-span-full text-center text-muted-foreground py-8">{t('portal.home.badgesTab.noMatch')}</p>}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {sorted.map((badge, idx) => (
                              <Card key={idx} onClick={() => setSelectedBadge(badge)} className={cn(
                                "p-4 rounded-xl transition-all cursor-pointer hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20",
                                badge.earned ? "bg-card border-green-200 dark:border-green-900/50" : "bg-muted/50 border-border opacity-60"
                              )}>
                                <div className="flex items-center gap-4">
                                  <div className="relative shrink-0">
                                    {badge.earned ? (
                                      <>
                                        {badge.icon && badge.icon.trim() ? (
                                          // eslint-disable-next-line @next/next/no-img-element
                                          <img src={badge.icon} alt={badge.name} className="w-12 h-12 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex'); }} />
                                        ) : null}
                                        {(!badge.icon || !badge.icon.trim()) && (
                                          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                            <Award className="w-6 h-6 text-amber-500" />
                                          </div>
                                        )}
                                        {badge.icon && badge.icon.trim() && (
                                          <div className="w-12 h-12 rounded-full bg-muted items-center justify-center hidden">
                                            <Award className="w-6 h-6 text-amber-500" />
                                          </div>
                                        )}
                                        <CheckCircle2 className="w-4 h-4 text-green-500 absolute -top-1 -end-1 bg-background rounded-full" />
                                      </>
                                    ) : (
                                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                        <Lock className="w-5 h-5 text-muted-foreground" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-sm text-foreground" dir="auto">{badge.name}</h4>
                                    <div className="mt-1 flex items-baseline gap-2">
                                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                                        {badge.earned ? t('portal.home.badge.earnedFor') : t('portal.home.badge.howToEarn')}
                                      </span>
                                      <p className="text-xs text-foreground/80 leading-snug truncate" dir="auto">
                                        {badge.description || t('portal.home.badge.defaultDescription')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))}
                            {sorted.length === 0 && <p className="text-center text-muted-foreground py-8">{t('portal.home.badgesTab.noMatch')}</p>}
                          </div>
                        );
                      })()}

                      {activePortalTab === 4 && showCertificatesTab && (() => {
                        // FIX 2026-05-12: removed `?? [hardcoded 3 fake certs]` fallback.
                        // Empty array → the existing "No certificates match the selected filter."
                        // empty state below handles it truthfully.
                        const allCerts = props.certificateCards ?? [];
                        const filtered = filterValue === 'all' ? allCerts :
                          filterValue === 'completed' ? allCerts.filter(c => c.status === 'Completed') :
                          allCerts.filter(c => c.status === 'In Progress');
                        const sorted = [...filtered].sort((a, b) =>
                          sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
                        );
                        return (
                          <>
                            <div className={cn(viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-3")}>
                              {sorted.map((cert, idx) => (
                                <Card key={idx} className="rounded-xl border-border cursor-pointer transition-all hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20 hover:scale-[1.01] py-0 gap-0">
                                  <CardContent className="p-6 flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                                      <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      {/* The course's name and the issue instant, worded in the reader's language. */}
                                      <p className="font-medium text-foreground" dir="auto">{t('portal.home.certificatesTab.name', { course: cert.name })}</p>
                                      <p className="text-xs text-muted-foreground">{fmt.date(cert.date) || t('portal.home.certificatesTab.pending')}</p>
                                    </div>
                                    <Badge variant={cert.status === 'Completed' ? 'default' : 'secondary'} className="rounded-full shrink-0">{enrollmentStatusLabel(t, cert.status)}</Badge>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                            {sorted.length === 0 && <p className="text-center text-muted-foreground py-8">{t('portal.home.certificatesTab.noMatch')}</p>}
                          </>
                        );
                      })()}
                      </div>
                    </div>
                    )}
                  </div>
                ) : (
                  /* Modern Blue Layout */
                  <div
                    className={cn(
                      "rounded-2xl overflow-hidden border bg-muted/50",
                      previewDarkMode ? "border-white/[0.08]" : "border-black/[0.05]"
                    )}
                    style={{
                      transform: `translateY(${scrollY * 0.05}px)`,
                      transition: 'transform 0.1s linear',
                    }}
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                          {employeeAvatar && employeeAvatar.trim() ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={employeeAvatar} alt={employeeName} className="w-24 h-24 rounded-full object-cover ring-4 ring-border shadow-lg" />
                          ) : (
                            <div className="w-24 h-24 rounded-full ring-4 ring-border shadow-lg bg-muted flex items-center justify-center text-2xl font-bold text-foreground">
                              {employeeMonogram}
                            </div>
                          )}
                          <div className="space-y-2">
                            <h1 className={cn("text-2xl font-semibold", previewTheme.endsWith('-dark') ? "text-white" : "text-foreground")}>{employeeName}</h1>
                            <div className={cn("flex items-center gap-2 text-sm", previewTheme.endsWith('-dark') ? "text-gray-400" : "text-muted-foreground")}>
                              <span>{employeeDepartment}</span>
                            </div>
                          </div>
                        </div>
                        <Card className={cn("border-b-4 border-b-destructive min-w-[200px]", previewTheme.endsWith('-dark') ? "bg-gray-900 border-gray-700" : "")}>
                          <CardContent className="p-4 flex items-center justify-between">
                            <span className={cn("text-sm font-medium", previewTheme.endsWith('-dark') ? "text-white" : "")}>{t('portal.home.risk.title')}</span>
                            <div className="flex items-center gap-1.5">
                              <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              {/* Tickets 898 / 1166: coarse band label only — the portal has
                                  no real 0-100 score, so never show a precise-looking number. */}
                              <span className="text-lg font-bold text-destructive">{employeeRiskLevelLabel ? riskLevelLabel(t, employeeRiskLevelLabel) : '—'}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats Cards block — hidden for every theme (ticket #190).
                    Originally only rendered for non-modern-teal themes; after
                    the Light/Dark consolidation the canonical layout never
                    shows this duplicate stats card so it stays hidden for both. */}
                {false && (
                  <Card className={cn("border-0 shadow-lg backdrop-blur", previewTheme.endsWith('-dark') ? "bg-gray-800/95" : "bg-card/95")}>
                    <CardContent className="p-8">
                      <div className={cn("flex flex-col md:flex-row items-start md:items-center gap-8 divide-y md:divide-y-0 md:divide-x", previewTheme.endsWith('-dark') ? "divide-gray-700" : "divide-border")}>
                        <div className="flex items-center gap-4 flex-1 pt-4 md:pt-0">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-md">
                            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </div>
                          <div>
                            <div className={cn("text-4xl font-bold", previewTheme.endsWith('-dark') ? "text-white" : "text-foreground")}>{fmt.number(employeeTotalPoints)}</div>
                            <div className={cn("text-sm", previewTheme.endsWith('-dark') ? "text-gray-400" : "text-muted-foreground")}>{t('portal.home.rewardPoints')}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 flex-1 md:ps-8 pt-4 md:pt-0">
                          <div>
                            <div className={cn("text-4xl font-bold", previewTheme.endsWith('-dark') ? "text-white" : "text-foreground")}>{employeeBadgeCount}</div>
                            <div className={cn("text-sm", previewTheme.endsWith('-dark') ? "text-gray-400" : "text-muted-foreground")}>{t('portal.home.badges')}</div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {(resolvedBadgePack?.badges.filter(b => !b.event.startsWith('level_')).slice(0, 6).map(b => b.icon) ?? []).map((badgeImg, i) => (
                              badgeImg && badgeImg.trim() ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img key={i} src={badgeImg} alt={t('portal.home.badgeNumber', { number: i + 1 })} className="w-10 h-10 object-contain transition-transform hover:scale-110" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex'); }} />
                              ) : (
                                <div key={i} className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                  <Award className="w-5 h-5 text-muted-foreground" />
                                </div>
                              )
                            ))}
                            <Button variant="link" className="text-xs text-primary">{t('portal.home.viewAll')}</Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* contentinfo landmark — page footer. Gives screen-reader users a
                    "content information" region to jump to and closes out the set
                    of top-level landmarks (banner / navigation / contentinfo) that
                    the /portal dashboard otherwise lacks once the (portal) layout
                    header is suppressed. */}
                <footer role="contentinfo" className="pt-8 pb-4 text-center text-xs text-muted-foreground">
                  <span>{t('portal.home.footer')}</span>
                </footer>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selectedBadge} onOpenChange={(o) => !o && setSelectedBadge(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden gap-0 shadow-[0_25px_80px_-15px_rgba(34,197,94,0.35)] dark:shadow-[0_25px_80px_-15px_rgba(34,197,94,0.55)] border-border/60" showCloseButton>
          {selectedBadge && (
            <>
              <div className={cn(
                "relative px-6 pt-10 pb-8 text-center overflow-hidden",
                selectedBadge.earned
                  ? "bg-gradient-to-b from-green-500/15 via-emerald-500/5 to-transparent"
                  : "bg-gradient-to-b from-slate-500/10 via-zinc-500/5 to-transparent"
              )}>
                <div
                  aria-hidden
                  className={cn(
                    "absolute inset-0 -z-10 blur-3xl opacity-60",
                    selectedBadge.earned ? "bg-green-500/20" : "bg-slate-500/10"
                  )}
                  style={{ clipPath: 'circle(40% at 50% 10%)' }}
                />
                <div className="mx-auto mb-5 relative inline-block">
                  {selectedBadge.earned ? (
                    <>
                      <div className="absolute inset-0 -m-3 rounded-full bg-gradient-to-br from-green-400/30 to-emerald-500/10 blur-xl" />
                      {selectedBadge.icon && selectedBadge.icon.trim() ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={selectedBadge.icon} alt={selectedBadge.name} className="relative w-28 h-28 object-contain drop-shadow-[0_8px_20px_rgba(34,197,94,0.45)]" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex'); }} />
                      ) : null}
                      {(!selectedBadge.icon || !selectedBadge.icon.trim()) && (
                        <div className="relative w-28 h-28 rounded-full bg-muted flex items-center justify-center">
                          <Award className="w-14 h-14 text-amber-500" />
                        </div>
                      )}
                      {selectedBadge.icon && selectedBadge.icon.trim() && (
                        <div className="relative w-28 h-28 rounded-full bg-muted items-center justify-center hidden">
                          <Award className="w-14 h-14 text-amber-500" />
                        </div>
                      )}
                      <div className="absolute -top-1 -end-1 w-7 h-7 rounded-full bg-background flex items-center justify-center ring-2 ring-background">
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      </div>
                    </>
                  ) : (
                    <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center ring-4 ring-background/50">
                      <Lock className="w-11 h-11 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <DialogTitle className="text-2xl font-bold tracking-tight mb-2" dir="auto">
                  {selectedBadge.name}
                </DialogTitle>
                <DialogDescription asChild>
                  <div>
                    <span className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold",
                      selectedBadge.earned ? "bg-green-500/15 text-green-500" : "bg-muted text-muted-foreground"
                    )}>
                      <span className={cn("w-1.5 h-1.5 rounded-full", selectedBadge.earned ? "bg-green-500" : "bg-muted-foreground")} />
                      {selectedBadge.earned ? t('portal.home.badge.earned') : t('portal.home.badge.locked')}
                    </span>
                  </div>
                </DialogDescription>
              </div>

              <div className="px-6 pb-6 space-y-3 text-start">
                <div className="rounded-lg border border-border p-4 bg-muted/30">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                    {selectedBadge.earned ? t('portal.home.badge.whatYouDid') : t('portal.home.badge.howToEarnThis')}
                  </div>
                  <p className="text-sm text-foreground leading-relaxed" dir="auto">
                    {selectedBadge.description || t('portal.home.badge.defaultDescription')}
                  </p>
                </div>

                {selectedBadge.earned && (selectedBadge.relatedTraining || selectedBadge.pointsEarned) && (
                  <div className="rounded-lg border border-green-500/30 p-4 bg-green-500/5">
                    <div className="text-[11px] uppercase tracking-wide text-green-500 mb-2">{t('portal.home.badge.awardedFor')}</div>
                    {selectedBadge.relatedTraining && (
                      <p className="text-sm text-foreground mb-1">
                        <span className="text-muted-foreground">{t('portal.home.badge.training')}</span>{' '}
                        <bdi className="font-medium">{selectedBadge.relatedTraining}</bdi>
                      </p>
                    )}
                    {selectedBadge.pointsEarned !== undefined && selectedBadge.pointsEarned > 0 && (
                      <p className="text-sm text-foreground">
                        <span className="text-muted-foreground">{t('portal.home.badge.pointsEarned')}</span>{' '}
                        <span className="font-semibold text-green-500">+{selectedBadge.pointsEarned}</span>
                      </p>
                    )}
                  </div>
                )}

                {selectedBadge.earned && selectedBadge.earnedAt && (
                  <div className="rounded-lg border border-border p-4 bg-muted/30">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{t('portal.home.badge.received')}</div>
                    <p className="text-sm text-foreground">{fmt.date(selectedBadge.earnedAt)}</p>
                    {selectedBadge.eventCount && selectedBadge.eventCount > 1 && (
                      <p className="text-xs text-muted-foreground mt-0.5">{t('portal.home.badge.earnedTimes', { count: selectedBadge.eventCount })}</p>
                    )}
                  </div>
                )}

                {!selectedBadge.earned && (
                  <div className="rounded-lg border border-amber-500/30 p-4 bg-amber-500/5">
                    <div className="text-[11px] uppercase tracking-wide text-amber-500 mb-1">{t('portal.home.badge.nextStep')}</div>
                    <p className="text-sm text-foreground">{t('portal.home.badge.nextStepBody')}</p>
                  </div>
                )}
              </div>
              <div className="px-6 pb-6 flex items-center justify-end gap-3">
                <Button variant="ghost" onClick={() => setSelectedBadge(null)}>{t('common.close')}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedNews} onOpenChange={(o) => !o && setSelectedNews(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0 max-h-[85vh] flex flex-col" showCloseButton>
          {selectedNews && (
            <>
              <div className="relative h-56 shrink-0 overflow-hidden">
                {selectedNews.image && selectedNews.image.trim() ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={selectedNews.image} alt={selectedNews.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center text-sm text-muted-foreground font-medium">
                    {newsCategoryLabel(selectedNews.category)}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                <Badge variant="outline" className="absolute top-4 start-4 rounded-full text-[10px] px-2 bg-white/10 backdrop-blur-sm text-white border-white/20">
                  {newsCategoryLabel(selectedNews.category)}
                </Badge>
              </div>
              <div className="px-6 pt-4 pb-2 shrink-0">
                <DialogTitle className="text-2xl font-bold tracking-tight mb-2 leading-tight" dir="auto">
                  {selectedNews.title}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {selectedNews.publishedAt && (
                      <>
                        <span>{fmt.date(selectedNews.publishedAt)}</span>
                        <span>·</span>
                      </>
                    )}
                    <span>{t('portal.home.updates.readTime', { minutes: readingMinutes(selectedNews.content || selectedNews.description) })}</span>
                  </div>
                </DialogDescription>
              </div>
              <div className="px-6 py-4 overflow-y-auto flex-1">
                {/* News content is sanitized server-side via HtmlSanitizerService
                    (BE Phase 5.5 hardening — see portal-news.service.ts).
                    The article-detail route at /portal/news/[id]/page.tsx
                    renders the same way. Without dangerouslySetInnerHTML
                    here, JSX text interpolation would auto-escape the HTML
                    so users saw literal `<p>foo</p>` strings instead of
                    rendered paragraphs/lists/etc. Fixed 2026-05-14 — ticket
                    #207. Falls back to .description (plain text) when no
                    .content is set. */}
                {selectedNews.content ? (
                  <div
                    // News is written by the customer, in whatever language:
                    // let it set its own direction (L-12).
                    dir="auto"
                    className="text-sm text-foreground/90 leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: selectedNews.content }}
                  />
                ) : (
                  <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line" dir="auto">
                    {selectedNews.description}
                  </p>
                )}
              </div>
              <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3 shrink-0">
                <Button variant="ghost" onClick={() => setSelectedNews(null)}>{t('common.close')}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
